import Decimal from "decimal.js";
import i18n from "../utils/i18n";
import { WorkflowId } from "./constants";
import { supportedLngs } from "../utils/i18n";
import { Node, Edge } from 'reactflow';

export type LanguageCode = keyof typeof supportedLngs;

// プロジェクトコンテキスト型定義
export interface ProjectContextType {
  userPermissions: UserPermissionType[];
  reviewDate: string | null;       // 振り返り日（null = 通常モード）
  isReviewMode: boolean;           // 振り返りモードか否か
  currencyData: Currency[];        // 通貨マスタ
  // 通貨マスタ取得結果（ok: 取得済み / empty: 0件=基本情報未保存 / error: 取得失敗 / undefined: 未取得・新規案件）
  currencyStatus?: CurrencyFetchStatus;
}

export type CurrencyFetchStatus = 'ok' | 'empty' | 'error';

// 振り返り一覧アイテム型
export interface ReviewListItem {
  reviewDate: string;
  reviewPhase: string;
  createDate: string;
}


// 共通画面-プロジェクト情報
export interface CapExRequest {
  // プロジェクトID
  projectId?: string | null;
  // 設備投資PROJECT名
  projectName?: string | null | undefined;
  // 起案者
  registrant?: string | null | undefined;
  // 申請日
  applicationDate?: string | null | undefined;
  // 承認ステータス
  approveStatus?: string | null | undefined;
  // 事業部階層
  divisionHierarchy?: string | null | undefined;
  // 事業部階層En
  divisionHierarchyEn?: string | null;
  // 目的
  purpose?: string | null | undefined;
  // 設備用途区分（"1"=専用設備 / "2"=汎用設備）
  equipmentUsageType?: string | null | undefined;
  // 現在の承認待ち情報（例: "製品統括: 山田 太郎"）
  currentApproverInfo?: string | null | undefined;
  // 並列承認の完了状況（JSON文字列）
  parallelApprovalStatus?: string | null | undefined;
  // CC要否
  crossCheckRequired?: boolean;
  // 資材担当要否
  materialOfficerRequired?: boolean;
}

/**
 * ブックマーク登録キー（ローカルストレージ保存用）
 */
export interface BookmarkKey {
  projectId: string;
  reviewDate: string | null;
}

/**
 * ブックマーク案件一覧の1件（getBookmarkProjectListの返却型）
 */
export interface BookmarkProjectItem {
  projectId: string;
  reviewDate?: string | null;
  projectName?: string | null;
  purpose?: string | null;
  equipmentUsageType?: string | null;
  divisionHierarchy?: string | null;
  customerName?: string | null;
  opportunityName?: string | null;
  registrantName?: string | null;
  applicationDay?: string | null;
  approvalStatus?: string | null;
  // 削除済み案件か（true時は詳細画面への遷移不可）
  deleted?: boolean | null;
  // 閲覧権限なしか（true時は詳細画面への遷移不可）
  accessDenied?: boolean | null;
}

/**
 * 振り返り依頼一覧の1件（マージ後）
 * - requestType: "review"（起案者向け）/ "confirmation"（振り返り入力・確認依頼）/ "approval"（振り返り承認依頼）
 * - 優先度: approval > confirmation > review で最終表示対応種別を決定
 */
export type ReviewRequestType = "review" | "confirmation" | "approval";

export interface ReviewRequestItem {
  projectId: string;
  projectName?: string | null;
  registrantName?: string | null;
  divisionHierarchy?: string | null;
  divisionHierarchyEn?: string | null;
  purpose?: string | null;
  // 設備用途区分（"1"=専用設備 / "2"=汎用設備）
  equipmentUsageType?: string | null;
  // 対象となる振り返り日（YYYY-MM-DD）
  targetReviewDate: string;
  // フェーズ名（reviewSchedule.phase）
  reviewPhase?: string | null;
  // 今日 − 対象振り返り日（正=N日経過、0=本日）
  daysOverdue: number;
  // 振り返りステータス（"inProgress" | "notStarted"）
  reviewStatus?: string | null;
  // 対応種別（マージ結果、最終ロール優先）
  requestType?: ReviewRequestType;
  // 振り返りWFの承認ステータス（confirmation/approval時のみ）
  approvalStatus?: string | null;
  // 承認待ち担当テキスト（approval時のみ）
  currentApproverInfo?: string | null;
  // 並列承認状況JSON（並列フェーズのみ）
  parallelApprovalStatus?: string | null;
  // CC要否
  crossCheckRequired?: boolean;
  // 資材担当要否
  materialOfficerRequired?: boolean;
}

/**
 * 振り返り検索結果の1件（searchReviewList の返却要素）
 */
export interface ReviewSearchItem {
  projectId: string;
  applicationNumber: string;
  projectName: string;
  opportunityName: string;
  mmiProductName: string;
  purpose: string;
  equipmentUsageType?: string | null;
  divisionHierarchy: string;
  divisionHierarchyEn?: string | null;
  registrantName: string;
  targetReviewDate: string;
  reviewPhase: string;
  reviewStatus: "inProgress" | "notStarted";
  approvalStatus: string | null;
  daysOverdue: number;
}

/**
 * 振り返り検索API（searchReviewList）の入力
 */
export interface SearchReviewListInput {
  applicationNumber?: string;
  projectName?: string;
  opportunityName?: string;
  mmiProductName?: string;
  purpose?: string;
  filterOrganizationIds?: string[];
  registrantEmployeeId?: string;
  targetReviewDateStart?: string;
  targetReviewDateEnd?: string;
  reviewStatusFilter?: "all" | "inProgress" | "notStarted";
  onlyMyAssignment?: boolean;
  onlyMyOriginatedProject?: boolean;
  onlyNotStartedReview?: boolean;
}

/**
 * 承認者一覧情報
 */
export interface ApproverList {
  /** 従業員ID */
  employeeId?: string | null;

  /** 従業員名 */
  employeeName?: string | null | undefined;

  /** 組織ID（主所属） */
  organizationId?: string | null | undefined;

  /** 組織名（複数所属の場合はスラッシュ区切り） */
  organizationName?: string | null | undefined;

  /** 役職ID */
  positionId?: string | null | undefined;

  /** 役職名 */
  positionName?: string | null | undefined;

  /** 枝番号 */
  brNo?: number | undefined;

  /** ワークフローID */
  flowId?: string | null | undefined;

  /** 枝番号 */
  flowOrder?: number | undefined;

  /** 確認フラグ */
  confirm?: boolean | null | undefined;

  /** 確認日付 */
  confirmTime?: string | null | undefined;

  /** 承認結果 */
  approvalResult?: string | null | undefined;

  /** コメント */
  comment?: string | null | undefined;

  /** カテゴリ名（CC確認の部門名等） */
  categoryName?: string | null | undefined;
}

/**
 * 画面パーミッション
 */
export interface PagePermissions {
  /** 記入者確認ボタン活性 */
  eabledRegistrant: boolean,
  /** 事業部確認ボタン活性 */
  eabledCompanyDivision: boolean,
  /** 事業部本部確認ボタン活性 */
  eabledCompanyHeadDivision: boolean,
  /** 営業確認ボタン活性 */
  eabledSalesDivision: boolean,
  /** 調達確認ボタン活性 */
  eabledProcurementDivision: boolean,
  /** 起案申請ボタン活性 */
  eabledApprovalRequest: boolean,
  /** 承認ボタン活性 */
  eabledApproval: boolean,
  /** PDFダウンロードボタン活性 */
  eabledPdfDownload: boolean,
  /** 編集可不可 */
  isEditable: boolean;
  /** 現在のワークフローID */
  currentFlowId?: (typeof WorkflowId)[keyof typeof WorkflowId];
}

/**
 * 従業員マスタ
 */
export interface Employee {
  /** 従業員ID */
  employeeId?: string;

  /** 従業員名 */
  employeeName?: string;

  /** 事業部ID */
  divisionId: string;

  /** 事業部名 */
  divisionName: string;

  /** 組織分類ID（確認担当者判定用） */
  organizationClassId?: string;

  /** 親組織ID（確認担当者判定用） */
  parentOrganizationId?: string;

  /** 子孫組織の組織分類IDリスト（確認担当者判定用） */
  descendantOrganizationClassIds?: string[];

  /** 役職名 */
  positionName?: string;
}

/**
 * 通貨マスタ
 */
export interface Currency {
  /** 通貨ID */
  currencyId?: string;

  /** 通貨名 */
  currencyName?: string;

  /** 為替レート */
  exchangeRate?: number;

  /** 表示順 */
  order?: number;

  /** 更新日時（楽観的ロック用。既存アイテム更新時にDBの値と照合する） */
  updateDate?: string;
}

/**
 * １０か条チェックリストマスタ
 */
export interface ChecklistRecord {
  /** ノードID (UUID型、主キー) */
  nodeId: string;

  /** 親ノードID (大項目の場合は null, 子項目の場合は親の nodeId) */
  parentNodeId: string | null;

  /** ノード種別 ('PRINCIPLE': 大項目 / 'ITEM': 子項目) */
  nodeClass: 'PRINCIPLE' | 'ITEM';

  /** NO. (条項番号) */
  checklistNo: string;

  /** コンテンツ名（日本語）※画面上の「１０か条」または「具体確認項目」の文言が入る */
  contentNameJa: string;

  /** コンテンツ名（英語）※画面上の「１０か条」または「具体確認項目」の文言が入る */
  contentNameEn: string;

  /** 相反する検討内容 (日本語) ※「Ja」付きがプロジェクトの正 */
  conflictingConsiderationsJa: string[];

  /** 相反する検討内容 (英語) */
  conflictingConsiderationsEn: string[];

  /** 並び順 (1から始まる連番) */
  order: number;

  /** 論理削除フラグ (マスタメンテ画面で使用) */
  deleted: boolean;

  /** 作成日時 */
  createDate?: string;

  /** 更新日時 */
  updateDate?: string;
}

/**
 * 国籍マスタ
 */
export interface NationalityType {
  /** 国籍ID (ISO 3166-1 alpha-3) */
  nationalityId: string;
  /** 国籍名（日本語） */
  nationalityNameJpn: string;
  /** 国籍名（英語） */
  nationalityNameEng: string;
  /** 削除フラグ */
  deleted: boolean;
  /** 表示順 */
  order: number;
  /** 作成日時 */
  createDate: string;
  /** 更新日時 */
  updateDate: string;
}

/**
 * お知らせマスタ
 */
export interface NotificationType {
  /** お知らせID (UUID) */
  notificationId: string;
  /** タイトル（日英） */
  title: BilingualName;
  /** 本文（日英） */
  body: BilingualName;
  /** 公開日 (YYYY-MM-DD) */
  publishDate: string;
  /** 公開フラグ */
  isPublished: boolean;
  /** 表示順 */
  order: number;
  /** 削除フラグ */
  deleted: boolean;
  /** 作成日時 */
  createDate: string;
  /** 更新日時 */
  updateDate: string;
}

/**
 * 実勢レート（月次最新）
 */
export interface LatestCurrency {
  /** 通貨ID */
  currencyId: string;

  /** 通貨名 */
  currencyName: string;

  /** 為替レート */
  exchangeRate: number;

  /** 適用月（YYYY-MM-DD形式） */
  rateMonth: string;
}

/**
 * 計画レート（見込み）
 */
export interface CurrencyProspect {
  /** 通貨ID */
  currencyId: string;

  /** 通貨名 */
  currencyName: string;

  /** 為替レート */
  exchangeRate: number;

  /** 適用年 */
  rateYear: string;

  /** バージョン */
  version: number;
}

/**
 * 量産拠点マスタ
 */
export interface MpLocation {
  /** 量産拠点ID */
  MPLocationId?: string
  /** 量産拠点名 */
  MPLocationName?: string
  /** 削除フラグ */
  deleted?: boolean
}

/** 言語コードをキーに持つ多言語名オブジェクト（例: { ja: "...", en: "..." }） */
export type BilingualName = {
  [key in LanguageCode]?: string;
} & {
  /** 将来の言語追加やRecord<string, unknown>系の汎用ヘルパーとの互換のためのインデックスシグネチャ */
  [key: string]: string | undefined;
};

export interface EquipmentCategoryType {
  /** 設備分類ID（CAT-XX） */
  equipmentCategoryId?: string,
  /** 設備分類名（多言語） */
  equipmentCategoryName?: BilingualName,
  /** 対象プロダクト・用途 */
  description?: string,
  /** コードプレフィックス（JIS分類コード） */
  codePrefix?: string,
  /** 表示順 */
  sortOrder?: number,
  /** 仮登録フラグ */
  isCandidate?: boolean,
  /** 登録者のユーザーID（email） */
  registeredBy?: string,
  /** 削除フラグ */
  deleted?: boolean,
  /** 作成日時 */
  createDate?: string,
  /** 更新日時 */
  updateDate?: string,
};

/**
 * 主要装置・機械マスタ
 */
export interface EquipmentGroupType {
  /** 装置・機械ID */
  equipmentGroupId?: string,
  /** 装置・機械名（多言語） */
  equipmentGroupName?: BilingualName,
  /** 所属設備分類ID（CAT-XX） */
  equipmentCategoryId?: string,
  /** 設備分類内の表示順 */
  sortOrder?: number,
  /** 仮登録フラグ（true=仮登録、false/未設定=正式） */
  isCandidate?: boolean,
  /** 仮登録者のユーザーID（email） */
  registeredBy?: string,
  /** 削除フラグ */
  deleted?: boolean,
  /** 作成日時 */
  createDate?: string,
  /** 更新日時 */
  updateDate?: string,
};

/**
 * 事業部マスタ
 * @deprecated RDS移行後は OrganizationWithHierarchyType を使用してください
 */
export interface DivisionType {
  /** 事業部ID */
  divisionId?: string,
  /** 事業部名 */
  divisionName?: string,
  /** 事業本部名 */
  divisionHqName?: string,
  /** コメント */
  comment?: string,
  /** 削除フラグ */
  deleted?: boolean,
  /** 作成日時 */
  createDate?: string,
  /** 更新日時 */
  updateDate?: string,
};

/**
 * 組織マスタ（階層構造付き）
 * RDS: r_mm_organization テーブル
 * API: getOrganizationWithHierarchy
 */
export interface OrganizationWithHierarchyType {
  /** 組織ID */
  organizationId: string;
  /** 組織名 */
  organizationName: string;
  /** 組織名En */
  organizationNameEn?: string;
  /** 親組織ID */
  parentOrganizationId: string | null;
  /** 親組織名 */
  parentOrganizationName: string;
  /** 適用年度（YYYY-MM形式） */
  fiscalPeriod?: string;
};

/**
 * 管理体系マスタ（マスタメンテナンス用）
 * DynamoDB: M_CEAS_ACCOUNTING_ORGANIZATION テーブル
 * API: getAccountingOrganization, getAccountingOrganizationById
 */
export interface AccountingOrganizationType {
  /** 組織ID (PK) */
  organizationId?: string;
  /** 適用年度 (SK) YYYY-MM形式 */
  fiscalPeriod?: string;
  /** 組織名 */
  organizationName?: string;
  /** 組織名（英語） */
  organizationNameEn?: string;
  /** 親組織ID */
  parentOrganizationId?: string;
  /** コメント */
  comment?: string;
  /** 表示順 */
  order?: number;
  /** 削除フラグ */
  deleted?: boolean;
  /** 作成日時 */
  createDate?: string;
  /** 更新日時 */
  updateDate?: string;
}

/**
 * 会社/工場マスタ
 * DynamoDB: M_CEAS_COMPANY_PLANT テーブル
 * API: getCompanyPlant
 */
export interface CompanyPlantType {
  /** 会社/工場ID */
  companyPlantId: string;
  /** 会社/工場名 */
  companyPlantName: string;
  /** 会社/工場名（英語） */
  companyPlantNameEn?: string;
  /** 親会社/工場ID */
  parentCompanyPlantId: string | null;
  /** 会社コード */
  companyCode?: string;
  /** 通貨ID */
  currencyId?: string;
  /** 所在国 */
  locationCountry?: string;
  /** 金利マスタ地域コード */
  areaCode?: string;
  /** 基準金利（互換性のため残す、金利マスタから取得した値を保存） */
  baseInterestRate?: number;
  /** 税率 */
  taxRate?: number;
  /** 表示順 */
  order?: number;
  /** 削除フラグ */
  deleted?: boolean;
  /** 作成日時 */
  createDate?: string;
  /** 更新日時 */
  updateDate?: string;
};

/**
 * 会社/工場スナップショット
 */
export interface CompanyPlantSnapshotType {
  /** 会社名 */
  companyName?: string;
  /** 会社名En */
  companyNameEn?: string;
  /** 工場名 */
  plantName?: string;
  /** 工場名En */
  plantNameEn?: string;
  /** 通貨ID */
  currencyId?: string;
  /** 所在国 */
  locationCountry?: string;
  /** 金利マスタ地域コード */
  areaCode?: string;
  /** 税率 */
  taxRate?: number | null;
};

/** 会社/工場スナップショット（複数選択対応リスト） */
export interface CompanyPlantSnapshotListType {
  /** スナップショットリスト（1件以上） */
  items: CompanyPlantSnapshotType[];
  /** 有効な通貨ID（全件同一の場合はitemsの値、異なる場合はユーザー選択値） */
  activeCurrencyId: string;
  /** 有効な税率（全件同一の場合はitemsの値、異なる場合はユーザー選択値） */
  activeTaxRate: number | null;
  /** 有効な金利マスタ地域コード（全件同一の場合はitemsの値、異なる場合はユーザー選択値） */
  activeAreaCode: string;
}

/**
 * 材料マスタ
 */
export interface Material {
  /** 材料ID */
  materialId?: string;
  /** 材料名 */
  materialName?: BilingualName;
  /** 大分類 */
  category?: BilingualName;
  /** 表示順 */
  sortOrder?: number;
  /** 削除フラグ */
  deleted?: boolean;
}

/**
 * 競合マスタ
 */
export interface Competitor {
  /** 競合ID */
  competitorId?: string;
  /** 競合名 */
  competitorName?: string;
  /** 削除フラグ */
  deleted?: boolean;
}

/**
 * MM製品カテゴリに紐づく競合リスト
 */
export interface MMProductCompetitor {
  /** 競合IDリスト */
  competitorList?: string[];
}

export type AveragePeriod = "avg12" | "avg24" | "avg36";

/**
 * プロジェクト情報(RDSあり)
 */
export interface ProjectWithRdsData {
  /** プロジェクトID */
  projectId?: string | null;
  /** 設備投資PROJECT名 */
  projectName?: string | null | undefined;
  /** MMI製品名 */
  mmProductNameFreeWrite?: string | null | undefined;
  /** 顧客製品名(変更時のみ保存) */
  revisedCustomerProductNameByRevision?: string | null | undefined;
  /** 案件名(変更時のみ保存) */
  revisedOpportunityNameByRevision?: string | null | undefined;
  /** 目的 */
  purpose?: string | null | undefined;
  /** 投資目的 */
  purposeOfInvestment?: string | null | undefined;
  /** 車種モデル */
  carModel?: string | null | undefined;
  /** 顧客業界 */
  customerIndustry?: string | null | undefined;
  /** 顧客業界階層（全階層の名前を " / " 区切りで保存） */
  customerIndustryHierarchy?: string | null | undefined;
  /** 顧客国籍 */
  customerNationality?: string | null | undefined;
  /** 申請番号 */
  applicationNumber?: string | null | undefined;
  /** 設備計画番号（複数） */
  equipmentPlanNumber?: string[] | null | undefined;
  /** 計画予算（内/外） */
  plannedBudget?: string | null | undefined;
  /** 予算額（円） */
  budgetAmount?: number | null | undefined;
  /** 設備用途区分（専用/汎用） */
  equipmentUsageType?: string | null | undefined;
  /** 事業部階層（全階層を / 区切りで保存） */
  divisionHierarchy?: string | null | undefined;
  /** 事業部階層En（全階層を / 区切りで保存） */
  divisionHierarchyEn?: string | null;
  /** 申請書様式（"detail" / "simple"） */
  applicationFormType?: string | null | undefined;
  /** 損益表示（"show" / "hide"）※簡易様式のときのみ有効 */
  profitLossDisplayType?: string | null | undefined;
  /** 閲覧制御 */
  screenViewRestriction?: string | null | undefined;
  /** 申請月度 */
  applicationMonth?: string | null | undefined;
  /** 量産開始日（旧：プロジェクト開始日（投資設備検収日）） */
  projectStartDate?: string | null | undefined;
  /** 計上期間終了日（画面表示名は変更済みだが、変数名は変更しない。販売数量・単価/損益試算/Simulationのデータ入力期間の終端を表し、設備の実際のEOL予定日とは異なる） */
  projectEndDate?: string | null | undefined;
  /** EOL予定日（設備の実際のEOL予定日。計上期間の終端を表す projectEndDate とは意味が異なるため注意） */
  eolDate?: string | null | undefined;
  /** 設備区分（カンマ区切り） */
  equipmentCategory?: string | null | undefined;
  /** 増産投資量産開始日 */
  increasedProductionStartDate?: string | null | undefined;
  /** 更新投資量産開始日 */
  updateAndOtherStartDate?: string | null | undefined;
  /** 合理化投資量産開始日 */
  rationalizationStartDate?: string | null | undefined;
  /** 新製品投資量産開始日 */
  newProductStartDate?: string | null | undefined;
  /** 承認者レコード */
  approverRecords?: string[] | null | undefined;
  /** 金型費売上（プロジェクト別） */
  moldSalesByProject?: string | null | undefined;
  /** 開発費売上（プロジェクト別） */
  developmentSalesByProject?: string | null | undefined;
  /** 設備費売上（プロジェクト別） */
  equipmentSalesByProject?: string | null | undefined;
  /** 仕様・性能 */
  specificationAndPerformance?: string | null | undefined;
  /** 単価契約（合意） */
  pricingAgreement?: string | null | undefined;
  /** 顧客スケジュール */
  customerSchedule?: string | null | undefined;
  /** 設備設置スケジュール */
  equipmentInstallationSchedule?: string | null | undefined;
  /** P&L読込ファイル名 */
  profitAndLossFileName?: string | null | undefined;
  /** その他の設備費 */
  otherEquipmentCost?: string | null;
  /** 過去PJの投資対象設備費 */
  pastEquipmentCost?: string | null;
  /** 過去PJの投資対象設備 通貨ID */
  pastEquipmentCostCurrencyId?: string | null;
  /** 開発費 */
  devCost?: string | null;
  /** その他の経費 */
  otherCost?: string | null;

  /** 月別費用データ */
  monthlyExpenseData?: string | null;
  /** 生産工場労務費単価 */
  productionFactoryLaborUnitPrice?: number | null;
  /** 生産工場経費単価 */
  productionFactoryExpenseUnitPrice?: number | null;

  /** 投資対象設備以外の減価償却費 通貨ID */
  otherEquipmentCostCurrencyId?: string | null;
  /** 外注加工費 通貨ID */
  outsourcingProcessingCostCurrencyId?: string | null;
  /** 工場建物の減価償却費 通貨ID */
  factoryBuildingDepreciationCurrencyId?: string | null;
  /** 国内工場労務費 通貨ID */
  domesticLaborCostCurrencyId?: string | null;
  /** 国内工場販管費 通貨ID */
  domesticSgaExpensesCurrencyId?: string | null;
  /** 生産工場販管費 通貨ID */
  productionSgaExpensesCurrencyId?: string | null;
  /** 国内工場経費 通貨ID */
  domesticFactoryExpensesCurrencyId?: string | null;
  /** 国内工場経費-投資対象設備 通貨ID */
  domesticInvestmentDepreciationCurrencyId?: string | null;
  /** 国内工場経費-工場建物 通貨ID */
  domesticFactoryBuildingDepreciationCurrencyId?: string | null;
  /** 国内工場経費-その他の設備 通貨ID */
  domesticOtherDepreciationCurrencyId?: string | null;
  /** 販社販管費 通貨ID */
  salesCompanySgaCurrencyId?: string | null;
  /** 開発費 通貨ID */
  devCostCurrencyId?: string | null;
  /** ロイヤリティ 通貨ID */
  royaltyCurrencyId?: string | null;
  /** その他経費 通貨ID */
  otherCostCurrencyId?: string | null;
  /** 生産工場労務費 通貨ID */
  productionLaborCostCurrencyId?: string | null;
  /** 海外工場経費 通貨ID */
  overseasFactoryExpensesCurrencyId?: string | null;
  /** オリジナル(当初損益) */
  originalProfitAndLoss?: string | null | undefined;
  /** オリジナル月別(当初損益月別) */
  originalProfitAndLossByMonth?: string | null | undefined;
  /** オリジナル(当初損益) - JPY版（二重通貨変換回避用、常にJPY固定） */
  displayOriginalProfitAndLoss?: string | null | undefined;
  /** オリジナル月別(当初損益月別) - JPY版（二重通貨変換回避用、常にJPY固定） */
  displayOriginalProfitAndLossByMonth?: string | null | undefined;
  /** 更新投資損益試算データ（ローカル通貨版） */
  updateInvestmentProfitLossData?: string | null | undefined;
  /** 更新投資損益試算データ - JPY版（二重通貨変換回避用、常にJPY固定） */
  displayUpdateInvestmentProfitLossData?: string | null | undefined;
  /** 合理化投資損益試算データ（ローカル通貨版） */
  rationalizationProfitLossData?: string | null | undefined;
  /** 合理化投資損益試算データ - JPY版（二重通貨変換回避用、常にJPY固定） */
  displayRationalizationProfitLossData?: string | null | undefined;
  /** ベース(基本損益) */
  baseProfitAndLoss?: string | null | undefined;
  /** ストレッチ(ストレッチ損益) */
  stretchProfitAndLoss?: string | null | undefined;
  /** ダウンサイド(ダウンサイド損益) */
  downsideProfitAndLoss?: string | null | undefined;
  /** Simulation（新製品製造）数量(当初計画) */
  quantitiesChartDataOriginal?: string | null | undefined;
  /** Simulation（新製品製造）数量(ダウンサイド) */
  quantitiesChartDataModified?: string | null | undefined;
  /** Simulation（新製品製造）価格と材料単価(当初計画) */
  mergedChartDataOriginal?: string | null | undefined;
  /** Simulation（新製品製造）価格と材料単価(ダウンサイド) */
  mergedChartDataModified?: string | null | undefined;
  /** Simulation（新製品製造）売上(当初計画) */
  salesChartData?: string | null | undefined;
  /** Simulation（新製品製造）売上(ダウンサイド) */
  salesChartDataModified?: string | null | undefined;
  /** Simulation（生産性向上）売上(プロジェクト全体投資回収用) */
  salesChartDataAll?: string | null | undefined;
  /** Simulation（生産性向上）売上(当初投資・回収用) */
  salesChartDataPast?: string | null | undefined;
  /** Simulation（新製品製造）投資回収(当初計画) */
  combinedChartData?: string | null | undefined;
  /** Simulation（新製品製造）投資回収(ダウンサイド) */
  combinedChartDataModified?: string | null | undefined;
  /** Simulation（生産性向上）投資回収(プロジェクト全体投資回収) */
  combinedChartDataAll?: string | null | undefined;
  /** Simulation（生産性向上）投資回収(当初投資・回収) */
  combinedChartDataPast?: string | null | undefined;
  /** Simulation（新製品製造）回収時期ｎヶ月目（当初計画） */
  monthOfRecoveryOriginal?: string | null | undefined;
  /** Simulation（新製品製造）回収時期ｎヶ月目（ダウンサイド） */
  monthOfRecoveryModified?: string | null | undefined;
  /** Simulation（生産性向上）回収時期ｎヶ月目（プロジェクト全体投資回収） */
  monthOfRecoveryAll?: string | null | undefined;
  /** Simulation（生産性向上）回収時期ｎヶ月目（当初投資・回収） */
  monthOfRecoveryPast?: string | null | undefined;
  /** Simulation（新製品製造）顧客需要と設備キャパシティ（当初計画） */
  gapChartDataOriginal?: string | null | undefined;
  /** Simulation（新製品製造）顧客需要と設備キャパシティ（ダウンサイド） */
  gapChartDataModified?: string | null | undefined;
  /** Simulation（新製品製造）設備生産数量からのギャップ（当初計画） */
  productionQuantityChartDataOriginal?: string | null | undefined;
  /** Simulation（新製品製造）設備生産数量からのギャップ（ダウンサイド） */
  productionQuantityChartDataModified?: string | null | undefined;
  /** Simulation（新製品製造）選択中のシミュレーション設定ID（カンマ区切り） */
  selectedSimulationConfigIds?: string | null | undefined;
  /** Simulation（新製品製造）売上（保存済み設定用・配列形式JSON） */
  salesChartDataForSavedConfig?: string | null | undefined;
  /** Simulation（新製品製造）投資回収（保存済み設定用・配列形式JSON） */
  combinedChartDataForSavedConfig?: string | null | undefined;
  /** Simulation（新製品製造）数量（保存済み設定用・配列形式JSON） */
  quantitiesChartDataForSavedConfig?: string | null | undefined;
  /** Simulation（新製品製造）価格と材料単価（保存済み設定用・配列形式JSON） */
  mergedChartDataForSavedConfig?: string | null | undefined;
  /** Simulation（新製品製造）回収時期（保存済み設定用・配列形式JSON） */
  monthOfRecoveryForSavedConfig?: string | null | undefined;
  /** Simulation（新製品製造）顧客需要と設備キャパシティ（保存済み設定用・配列形式JSON） */
  gapChartDataForSavedConfig?: string | null | undefined;
  /** Simulation（新製品製造）設備生産数量からのギャップ（保存済み設定用・配列形式JSON） */
  productionQuantityChartDataForSavedConfig?: string | null | undefined;
  /** Simulation 生産キャパグラフ（当初計画）。OFF時はnull */
  existingLineCapacityChartDataOriginal?: string | null | undefined;
  /** Simulation 生産キャパグラフ（ダウンサイド）。OFF時はnull */
  existingLineCapacityChartDataModified?: string | null | undefined;
  /** Simulation 生産キャパグラフ（保存済み設定用・配列形式JSON）。OFF時はnull */
  existingLineCapacityChartDataForSavedConfig?: string | null | undefined;
  /** Simulation（生産性向上）生産能力（ライン全体）（当初計画） */
  productionCapacityChartData?: string | null | undefined;
  /** Simulation（生産性向上）生産能力（ライン全体）（ダウンサイド） */
  productionCapacityChartDataModified?: string | null | undefined;
  /** Simulation（生産性向上）合理化前後サマリーチャートデータ */
  rationalizationSummaryChartData?: string | null | undefined;
  /** ダウンサイドリスク対応 */
  downsideRisk?: string | null;
  /** ダウンサイド指数 */
  downsideIndex?: string | null;
  /** ダウンサイド指数(有効・無効) */
  downsideIndexEnable?: string | null;
  /** 最終的な投資判断の理由 */
  finalInvestmentReason?: string | null;
  /** 製造ラインID */
  productionLineId?: string | null;
  /** 過去プロジェクトId */
  pastProjectId?: string | null;
  /** 過去プロジェクト名 */
  pastProjectName?: string | null;
  /** 過去プロジェクトの振り返り日（紐づけ時点の最新承認済み振り返り日。申請のみの場合はnull） */
  pastReviewDate?: string | null;
  /** 登録日時 */
  createDate?: string | null | undefined;
  /** 更新日時 */
  updateDate?: string | null | undefined;
  /** 承認ステータス */
  approvalStatus?: string | null | undefined;
  /** 通貨ID */
  currencyId?: string | null | undefined;
  /** 労務費のベースアップ率(%) */
  baseLaborCostIncreaseRate?: number;
  /** 販売費及び一般管理費(%) */
  salesManagementRate?: number;
  /** 申請日 */
  applicationDay?: string | null | undefined;
  /** 承認日 */
  approveDay?: string | null | undefined;
  /**数量・単価自由記入欄 */
  quantityPriceFreeText?: string | null | undefined;
  /** 事業の継続性の有無 */
  businessContinuity?: string | null | undefined;
  /** ここから RDS */
  /** 顧客ID */
  customerId?: string | null | undefined;
  /** 顧客名 */
  customerName?: string | null | undefined;
  /** 顧客フルネーム */
  customerFullName?: string | null | undefined;
  /** 案件ID */
  opportunityId?: string | null | undefined;
  /** 用途 */
  applicationCategory?: string | null | undefined;
  /** 案件名 */
  opportunityName?: string | null | undefined;
  /** 顧客製品ID */
  customerProductId?: string | null | undefined;
  /** 顧客製品名 */
  customerProductName?: string | null | undefined;
  /** MMI製品カテゴリID */
  mmProductCategoryId?: string | null | undefined;
  /** MMI製品カテゴリ名 */
  mmProductCategoryName?: string | null | undefined;
  /** MMIソースID */
  mmiSourceId?: string | null | undefined;
  /** MMIソース名 */
  mmiSourceName?: string | null | undefined;
  /** 量産拠点国 */
  mmLocationCountry?: string | null | undefined;
  /** 工場：1日あたりの稼働時間 */
  operationHoursPerDay?: number | undefined;
  /** 工場：1年間における稼働日数 */
  workingDaysInEachYear?: number | undefined;
  /** MMIソース通貨 */
  mmiSourceCurrencyId?: string | null | undefined;
  /** 工場：基準金利 */
  baseInterestRate?: number | null | undefined;
  /** 会社/工場ID（カンマ区切りで複数IDを保持（例："cp001,cp002,cp003"）） */
  companyPlantId?: string | null | undefined;
  /** 会社/工場スナップショット（CompanyPlantSnapshotListType のJSON文字列） */
  companyPlantSnapshot?: string | null | undefined;
  /** エンドユーザーID */
  endUserId?: string | null | undefined;
  /** エンドユーザー名 */
  endUserName?: string | null | undefined;
  /** 登録者従業員ID */
  registrantEmployeeId?: string | null | undefined;
  /** 登録者従業員名 */
  registrantEmployeeName?: string | null | undefined;
  /** 登録者部門ID */
  registrantDivisionId?: string | null | undefined;
  /** 登録者部門名 */
  registrantDivisionName?: string | null | undefined;
  /** 部門ID */
  divisionId?: string | null | undefined;
  /** 部門名 */
  divisionName?: string | null | undefined;
  /** 確認担当者従業員リスト */
  confirmEmployees?: Employee[] | null | undefined;
  /** クロスチェック要否 */
  crossCheckRequired?: boolean | null | undefined;
  /** クロスチェック対象部門（カンマ区切り） */
  crossCheckDepartments?: string | null | undefined;
  /** クロスチェック担当者（JSON文字列） */
  crossCheckAssignees?: string | null | undefined;
  /** 資材担当役員要否 */
  materialOfficerRequired?: boolean | null | undefined;
  /** 資材担当役員担当者（JSON文字列） */
  materialOfficerAssignees?: string | null | undefined;
  /** 承認者（JSON文字列） */
  approverAssignees?: string | null | undefined;
  /** 本部長検討会議長名 */
  hqReviewChairName?: string | null | undefined;
  /** RDS更新日 */
  updateDateRDS?: string | null | undefined;
}

/**
 * PartNo前方一致検索結果のPartNoごとの詳細（partNo・displayPartNoの対応）
 */
export interface PartNoDetail {
  /** パーツNo（実キー） */
  partNo?: string | null;
  /** 変更後パーツNo（表示名） */
  displayPartNo?: string | null;
}

/**
 * 過去プロジェクト情報
 */
export interface PastProjectData {
  /** プロジェクトID */
  projectId?: string | null;
  /** 設備投資PROJECT名 */
  projectName?: string | null | undefined;
  /** MMI製品名 */
  mmProductNameFreeWrite?: string | null | undefined;
  /** 顧客製品名(変更時のみ保存) */
  revisedCustomerProductNameByRevision?: string | null | undefined;
  /** 目的 */
  purpose?: string | null | undefined;
  /** プロジェクト開始日（投資設備検収日） */
  projectStartDate?: string | null | undefined;

  /** 登録日時 */
  createDate?: string | null | undefined;
  /** 申請日 */
  applicationDay?: string | null | undefined;
  /** 承認日 */
  approveDay?: string | null | undefined;
  /** 承認ステータス */
  approvalStatus?: string | null | undefined;

  /** ここから RDS */
  /** 顧客ID */
  customerId?: string | null | undefined;
  /** 顧客名 */
  customerName?: string | null | undefined;

  /** 顧客製品ID */
  customerProductId?: string | null | undefined;
  /** 顧客製品名 */
  customerProductName?: string | null | undefined;

  /** MMIソースID */
  mmiSourceId?: string | null | undefined;
  /** MMIソース名 */
  mmiSourceName?: string | null | undefined;

  /** パーツNo */
  partNo?: string | null | undefined;
  /** パーツNoごとの詳細（partNo・displayPartNoの対応、No.435対応） */
  partNoDetails?: PartNoDetail[] | null;
  /** 最新承認済み振り返り日（承認済み振り返りがない場合はnull） */
  reviewDate?: string | null;
}

/**
 * １０か条チェックリストデータ
 */
export interface CheckListDataType {
  /** No */
  [key: string]: {
    /** 具体確認項目 */
    checkItems: {
      /** 該当 */
      [subKey: string]: string,
    },
    /** メモ欄 */
    memo: string,
  }
};

/**
 * パーツNoマスタ
 */
export interface PartNo {

  /** プロジェクトID */
  projectId?: string,

  /** パーツNo */
  partNo?: string;

  /** 変更後パーツNo（表示専用。未設定時はpartNoにフォールバックして表示する） */
  displayPartNo?: string;

  /** セット販売での単価・数量入力フラグ（RDS r_ceas_part_no.is_set_sales_input から取得） */
  isSetSalesInput?: boolean;

  /** 数量と単価 */
  quantityAndPrice?: string | null | undefined;

  /** 金型売上（Part No単位） */
  moldSalesByPartNo?: string | null | undefined;

  /** 開発売上（Part No単位） */
  developmentSalesByPartNo?: string | null | undefined;

  /** 材料原価 */
  materialCost?: string | null | undefined;

  /** 内製部品の有無 */
  inhousePart?: string | null | undefined;

  /** 内製利益を考慮した材料費 */
  materialCostWithInhouseProfit?: string | null | undefined;

  /** 工程（複数） */
  processes?: string | null | undefined;

  /** 基本情報登録フラグ */
  basicInfoRegistered?: boolean | null,

  /** 作成日付 */
  createDate?: string | null | undefined;

  /** 更新日付 */
  updateDate?: string | null | undefined;

}

/**
 * 顧客取り決め事項
 */
export interface PricingAgreementFormType {
  /** 単価の取り決め */
  pricing: {
    /** 単価の取り決め選択 */
    pricingType: string,
    /** 一定期間（ｎヶ月間） */
    pricingDuration: number | null,
    /** 自由記入欄 */
    pricingComment: string | null,
  },
  /** 為替変動の取り決め */
  exchange: {
    /** 為替変動の取り決め選択 */
    exchangeType: string,
    /** 見直し（ｎヶ月に1度） */
    exchangeDuration: number | null,
    /** 見直し（パーセント） */
    exchangeThreshold: number | null,
    /** 自由記入欄 */
    exchangeComment: string | null,
  },
  /** 材料費変動の取り決め */
  material: {
    /** 材料費変動の取り決め選択 */
    materialType: string,
    /** 材料選択 */
    selectedMaterials: string[],
    /** その他材料（自由入力） */
    otherMaterials: BilingualName[],
    /** 自由記入欄 */
    materialComment: string | null,
  },
  /** 数量の取り決め */
  quantity: {
    /** 数量の取り決め選択 */
    quantityType: string,
    /** 数量未達時の取り決め */
    quantitySubType: string | null,
    /** 内示（ｎヶ月） */
    quantityDuration: number | null,
    /** 数量未達時の自由記入欄 */
    notReachedComment: string | null,
    /** 自由記入欄 */
    quantityComment: string | null,
  },
  /** 競合の有無 */
  competitor: {
    /** 競合の有無選択 */
    competitorType: string,
    /** 競合状態 */
    competitiorCondition: string | null,
    /** 競合社選択 */
    selectedCompetitors: string[],
    /** 競合会社名（OTHER） */
    competitorNameOther: string | null,
    /** 当社及び競合のシェア */
    shareOfCompetitorAndCompany: string | null,
    /** 当社シェア（0～100%） */
    sharePercentage: number | null,
  },
  /** ファイルアップロードオプション（true: アップロードあり, false: アップロードなし） */
  uploadOption?: boolean,
  /** その他の取り決め */
  otherAgreements?: OtherAgreementItem[],
};

/** その他の取り決め項目 */
export interface OtherAgreementItem {
  /** タイトル */
  title: string;
  /** 内容 */
  content: string;
}

/**
 * 顧客取り決め事項フォーマット文字列格納
 */
export interface PricingAgreementFormFormatType {
  /** 単価の取り決め */
  pricing?: string | null;
  /** 自由記入欄（単価の取り決め） */
  pricingComment: string | null,
  /** 為替変動の取り決め */
  exchange?: string | null;
  /** 自由記入欄（為替変動の取り決め） */
  exchangeComment: string | null,
  /** 材料費変動の取り決め */
  material?: string | null;
  /** 選択材料 */
  selectMaterial?: string | null;
  /** その他材料（自由入力） */
  otherMaterials?: string[];
  /** 自由記入欄（材料費変動の取り決め） */
  materialComment: string | null,
  /** 数量の取り決め */
  quantity?: string | null;
  /** 数量未達時の自由記入欄 */
  notReachedComment: string | null,
  /** 自由記入欄（数量の取り決め） */
  quantityComment: string | null,
  /** 競合の有無 */
  competitor?: string | null;
  /** 競合状態 */
  competitiorCondition?: string | null;
  /** 選択競合社 */
  selectCompetitor?: string | null;
  /** 当社及び競合のシェア */
  shareOfCompetitorAndCompany: string | null,
  /** 当社シェア（0～100%） */
  sharePercentage: number | null,
  /** その他の取り決め */
  otherAgreements?: OtherAgreementItem[],
};

/** 単価の取り決め */
export const pricingType = {
  /** */
  FirstTimeTransactionOnly: { value: "初回取引のみ", get name() { return i18n.t('common.constants.pricingType.FirstTimeTransactionOnly', { defaultValue: '初回取引のみ' }); } },
  /** 一定期間 */
  FixedPeriod: { value: "一定期間", get name() { return i18n.t('common.constants.pricingType.FixedPeriod', { defaultValue: '一定期間' }); } },
  /** 取り決めなし */
  NoArrangement: { value: "取り決めなし", get name() { return i18n.t('common.constants.pricingType.NoArrangement', { defaultValue: '取り決めなし' }); } },
  /** 上記以外 */
  Other: { value: "上記以外", get name() { return i18n.t('common.constants.pricingType.Other', { defaultValue: '上記以外' }); } },
};

/** 為替変動の取り決め */
export const exchangeType = {
  /** 変動応じる */
  SubjectToFluctuations: { value: "変動応じる", get name() { return i18n.t('common.constants.exchangeType.SubjectToFluctuations', { defaultValue: '変動応じる' }); } },
  /** 取り決めなし */
  NoArrangement: { value: "取り決めなし", get name() { return i18n.t('common.constants.exchangeType.NoArrangement', { defaultValue: '取り決めなし' }); } },
  /** 上記以外 */
  Other: { value: "上記以外", get name() { return i18n.t('common.constants.exchangeType.Other', { defaultValue: '上記以外' }); } },
};

/** 材料費変動の取り決め */
export const materialType = {
  /** 応じる（変動対応） */
  SubjectToFluctuations: { value: "応じる（変動対応）", get name() { return i18n.t('common.constants.materialType.SubjectToFluctuations', { defaultValue: '応じる（変動対応）' }); } },
  /** 取り決めなし */
  NoArrangement: { value: "取り決めなし", get name() { return i18n.t('common.constants.materialType.NoArrangement', { defaultValue: '取り決めなし' }); } },
  /** 上記以外 */
  Other: { value: "上記以外", get name() { return i18n.t('common.constants.materialType.Other', { defaultValue: '上記以外' }); } },
};

/** 数量の取り決め */
export const quantityType = {
  /** 取り決めあり */
  Arrangement: { value: "取り決めあり", get name() { return i18n.t('common.constants.quantityType.Arrangement', { defaultValue: '取り決めあり' }); }, confirmString: `` },
  /** 取り決めなし */
  NoArrangement: { value: "取り決めなし", get name() { return i18n.t('common.constants.quantityType.NoArrangement', { defaultValue: '取り決めなし' }); }, confirmString: `` },
  /** 上記以外 */
  Other: { value: "上記以外", get name() { return i18n.t('common.constants.quantityType.Other', { defaultValue: '上記以外' }); } },
};

/** 数量未達時の取り決め */
export const quantitySubType = {
  /** 内示 */
  Preliminary: { value: "内示保証", get name() { return i18n.t('common.constants.quantitySubType.Preliminary', { defaultValue: '内示' }); }, confirmString: `` },
  /** 取り決めなし */
  NoExpectation: { value: "取り決めなし", get name() { return i18n.t('common.constants.quantitySubType.NoExpectation', { defaultValue: '取り決めなし' }); }, confirmString: `` },
};

/** 競合の有無 */
export const competitorType = {
  /** 競合あり */
  Competitor: { value: "競合あり", get name() { return i18n.t('common.constants.competitorType.Competitor', { defaultValue: '競合あり' }); } },
  /** 競合なし */
  NoCompetitor: { value: "競合なし", get name() { return i18n.t('common.constants.competitorType.NoCompetitor', { defaultValue: '競合なし' }); } },
};

/** 競合状態 */
export const competitiorCondition = {
  /** 当社が1番手 */
  TopRank: { value: "当社が1番手", get name() { return i18n.t('common.constants.competitiorCondition.TopRank', { defaultValue: '当社が1番手' }); } },
  /** 当社が2番手以下 */
  LowerRank: { value: "当社が2番手以下", get name() { return i18n.t('common.constants.competitiorCondition.LowerRank', { defaultValue: '当社が2番手以下' }); } }
};

/** ファイルアップロード有無 */
export const uploadOptionType = {
  Upload: { value: true, get name() { return i18n.t('common.constants.uploadOptionType.Upload', { defaultValue: 'アップロードあり' }); } },
  NoUpload: { value: false, get name() { return i18n.t('common.constants.uploadOptionType.NoUpload', { defaultValue: 'アップロードなし' }); } },
};

/**
 * ProfitCenter
 */
export interface ProfitCenterDataType {
  /** ProfitCenterID */
  profitCenterId?: string;

  /** Step2未使用 */
  fixedAssetFileName?: string | null | undefined;

  /** Step2未使用 */
  bomFileName?: string | null | undefined;

  /** P&Lファイル名 */
  profitAndLossFileName?: string | null | undefined;

  /** Step2未使用 */
  totalBomTime?: string | null | undefined;

  /** Step2未使用 */
  totalDepreciation?: string | null | undefined;

  /** Step2未使用 */
  totalFactoryOverHeadwoDepreciation?: string | null | undefined;

  /** Step2未使用 */
  totalLaborCost?: string | null | undefined;

  /** Step2未使用 */
  totalOutsideProcessCost?: string | null | undefined;

  /** 事業部ID */
  divisionId?: string | null | undefined;

  /** 設置工場ID Step2未使用 */
  sourceId?: string | null | undefined;

  /** 製品GrID（MM） Step2未使用 */
  mmProductCategoryId?: string | null | undefined;

  /** RDS更新日付 */
  updateDateRDS?: string | null | undefined;

  /** 作成日付 */
  createDate?: string | null | undefined;

  /** 更新日付 */
  updateDate?: string | null | undefined;
};

/**
 * 製造ライン情報
 */
export interface ProductionLineType {
  /** 製造ラインID */
  productionLineId?: string,
  /** 製造ライン名 */
  productionLineName?: string,
  /** サイクルタイム */
  cycleTime?: number | null,
  /** 稼働日数 */
  workingDays?: string | null,
  /** 非稼働時間 */
  nonWorkingHours?: string | null,
  /** 歩留まり */
  yieldRate?: string | null,
  /** 増産前生産数量過去ベスト（ライン全体） */
  bestProductionQuantity?: string | null,
  /** 歩留改善分基準月（YYYY-MM形式） */
  improvementBaseMonthYield?: string | null,
  /** 稼働率改善分基準月（YYYY-MM形式） */
  improvementBaseMonthAvailability?: string | null,
  /** サイクル短縮分基準月（YYYY-MM形式） */
  improvementBaseMonthCycleTime?: string | null,
  /** 稼働日数（下書き） */
  draftWorkingDays?: string | null,
  /** 非稼働時間（下書き） */
  draftNonWorkingHours?: string | null,
  /** 基準月設定（下書き） */
  draftBaseMonth?: string | null,
  /** 更新日時 */
  updateDate?: string,
}

/**
 * ダウンサイド指数情報
 */
export interface DownsideIndexType {

  /** ダウンサイドID */
  downsideId?: string,

  /** 顧客製造分類ID */
  customerProductCategoryId?: string,

  /** 顧客製造分類名 */
  customerProductCategoryName?: string,

  /** 項目ID */
  itemId?: string,

  /** 項目名 */
  itemName?: string,

  /** 年数 */
  yearIndex?: number,

  /** ストレス指数 */
  stressIndex?: number,

  /** 更新日時 */
  updateDate?: string,

}

/**
 * ダウンサイド指数情報（有効・無効）
 */
export interface DownsideIndexEnableType {
  /** 項目名 */
  id?: string,
  /** 有効・無効 */
  enable?: boolean
}

/**
 * 権限情報
 */
export interface UserPermissionType {
  /** ワークフロー区分(0:確認、1:承認) */
  flowKb?: string | null | undefined;

  /** ワークフローID */
  flowId?: string | null | undefined;

  /** 従業員ID */
  employeeId?: string;

  /** 確認担当部署ID（事業部/事業管理/営業部/調達部） */
  divisionId?: string;
}

/** 案件情報（sf移植） */
export interface Opportunity {
  opportunityId?: string;
  opportunityName?: string;
  opportunityStage: IdNameInfo;
  // MMProductAmountPerMonth?: number;
  MMProductCategory?: OpportunityInfo
  // MMProductCategoryId?: string;
  // MMProductCategoryName?: string;
  MMProductQuantityPerMonth?: number;
  MMProductUnitPrice?: number;
  MPLocationList?: IdNameInfo[];
  // TTLAmountPerMonth?: number;
  TTLQtyPerMonth?: number;
  // TTLAmount?: number;
  // TTLQty?: number;
  // targetPrice?: number
  // agentId?: string;
  // agentName?: string;
  applicationCategory?: string;
  // comment?: string;
  competitorList?: IdNameInfo[];
  channelList?: ChannelItem[];
  // competitorUnitPrice?: number;
  // contactIdList?: [string];
  currency?: IdNameInfo;
  currentStatus?: string;
  customer: IdNameInfo;
  // customerDevelopmentCode?: string;
  customerProduct?: OpportunityInfo;
  // customerProductName?: string;
  // customerProjectName?: string;
  endUser?: IdNameInfo;
  // goodForYokoten?: string;
  massProduction?: string;
  // organizationId?: string;
  // organizationName?: string;
  // productCategoryId?: string;
  // productCategoryName?: string;
  // productPartNumber?: string;
  // productUnitId?: string;
  // productUnitName?: string;
  // projectStart?: string;
  salesPerson: IdNameInfo;
  salesUnit: IdNameInfo;
  scheduleUpdate?: string;
  // territory?: string
  unitPerSet?: number;
  // ownerHistory?: owner[]
  // updateOnDelivery?: string;
  // updateOnDevelopment?: string;
  // updateOnFourM?: string;
  // updateOnPrice?: string;
  // updateOnQuality?: string;
  partsNumber?: string;
  mileStone1?: string;
  mileStone2?: string;
  mileStone3?: string;
  mileStone4?: string;
  mileStone5?: string;
  mileStone6?: string;
  schedule1?: string;
  schedule2?: string;
  schedule3?: string;
  schedule4?: string;
  schedule5?: string;
  schedule6?: string;
  // createDate?: string;
  // updateDate?: string;
}

/** 案件詳細（TopicsModal）の商流1行分 */
export interface ChannelItem {
  /** 商流階位（'2nd' | '3rd' | '4th'） */
  channelLevel?: string;
  channelId?: string;
  /** 客先種別（0:未選択, 1:EMS, 2:代理店, 3:その他） */
  clientType?: number;
  clientName?: string;
}

/** 顧客製品情報（営業支援） */
export interface CustomerProduct {
  /** 顧客製品ID */
  customerProductId?: string | null | undefined;
  /** 顧客製品名 */
  customerProductName?: string | null | undefined;
  /** 顧客製品名（サブ） */
  customerProductSubName?: string | null | undefined;
  /** 顧客開発コード */
  customerDevelopmentCode?: string | null | undefined;
  /** 画像ファイル名 */
  imageFileName?: string | null | undefined;
  /** プロジェクト開始時期 */
  projectStart?: string | null | undefined;
  /** 量産開始時期 */
  massProduction?: string | null | undefined;
  /** TAM 年 */
  tamPerYear?: number | null | undefined;
  /** メモ */
  memo?: string | null | undefined;
  /** スケジュール */
  schedule?: string | null | undefined;
  /** 余地 */
  potential?: string | null | undefined;
  /** ステータス */
  status?: string | null | undefined;
}

/**
 * ユニークプロセス情報
 */
export interface UniqueProcessType {
  /** プロセスID */
  uniqueProcessId?: string | null;
  /** プロセス名 */
  uniqueProcessName?: string | null;
  /** 工程順序 */
  uniqueProcessSeq: number;
  /** レベル */
  uniqueLevel?: number | null;

  /** サイクルタイム/PartNo（秒） */
  uniqueProcessCycleTimeInSecondByPartNo?: string | null;
  /** 工程のまとまり */
  uniqueProcessStage?: string | null;

  // ※以降、DB項目外

  /** サイクルタイム/PartNo（配列展開後） */
  uniqueProcessCycleTimeInSecondByPartNoList?: CycleTimeInSecondByPartNoType[] | null;
  /** パーツNo */
  partNo?: string;
  /** スプレッドシートID */
  spreadsheetId?: string;

  /** 下位工程 */
  subProcess?: UniqueProcessType[] | null;

  /** 描画マトリクスＸ軸 */
  posX: number;
  /** 描画マトリクスＹ軸 */
  posY: number;
}

/**
 * サイクルタイム/PartNo
 */
export interface CycleTimeInSecondByPartNoType {
  /** パーツNo */
  partNo: string;
  /** サイクルタイム */
  cycleTime: string;
  /**
   * サイクルタイム(年月別データ) Record<"YYYY-MM" : [サイクルタイム]>
   * 例
   * {
   *   "2025-04" : "1.1234",
   *   "2025-05" : "1.1234",
   * }
   */
  cycleTimeMonthly: Record<string, string>;
  /** itemName */
  itemName?: string;
}

/**
 * 設備サプライヤー情報
 */
export interface EquipmentSupplierType {
  /** 設備サプライヤーID */
  equipmentSupplierId?: string | null;
  /** 設備サプライヤー名（多言語） */
  equipmentSupplierName?: BilingualName | null;
  /** 削除フラグ */
  deleted?: boolean,
  /** 仮登録フラグ */
  isCandidate?: boolean,
  /** 仮登録者ID */
  registeredBy?: string | null,
  /** 作成日時 */
  createDate?: string,
  /** 更新日時 */
  updateDate?: string,
}

/**
 * 設備サプライヤー(設備ID含)情報
 */
export interface EquipmentSupplierAndEqIdType {
  /** 設備サプライヤーID */
  equipmentSupplierId?: string | null;
  /** 設備サプライヤー名（多言語） */
  equipmentSupplierName?: BilingualName | null;
  /** 設備ID */
  equipmentId?: string | null;
}

/**
 * 設備情報
 */
export interface EquipmentType {
  /** プロジェクトID */
  projectId?: string | null;
  /** 設備ID */
  equipmentId?: string | null;
  /** 設備名 */
  equipmentName?: string | null;
  /** 固定資産コード */
  fixedAssetCode?: string | null;
  /** 発注予定日 */
  scheduledOrderDate?: string | null;
  /** 発注日（実際の日） */
  actualOrderDate?: string | null;
  /** 据付日 */
  installationDate?: string | null;
  /** 単価 */
  unitPrice?: number;
  /** 数量 */
  quantity?: number;
  /** トータル数量 */
  totalQuantity?: number;
  /** 数量単位 */
  quantityUnit?: string;
  /** 投資金額 */
  amount?: string;
  /** 簿価 */
  bookValue?: string;
  /** 設備に必要な人員数（単純工＋多能工＋テクニシャン） */
  numberOfPeopleRequiredForEquipment?: number;
  /** 設備に必要な人員数（単純工） */
  unskilledWorker?: number;
  /** 設備に必要な人員数（多能工） */
  multiSkilledWorker?: number;
  /** 設備に必要な人員数（テクニシャン） */
  technician?: number;
  /** 設備なし時に必要な人員数 */
  numberOfPeopleRequiredForWithoutEquipment?: number;
  /** 更新日時 */
  updateDate?: string;
  /** 償却年数 */
  depreciationPeriod?: number;
  /** 償却開始月 */
  depreciationStartMonth?: string;
  /** 検収月 */
  acceptanceMonth?: string;
  /** 申請予定月 */
  applicationPlannedMonth?: string;

  // RDSデータ
  /** 設備サプライヤーID */
  equipmentSupplierId?: string | null;
  /** 設備サプライヤー名（多言語） */
  equipmentSupplierName?: BilingualName | null;
  /** 設備グループID */
  equipmentGroupId?: string | null;
  /** 設備グループ名（多言語） */
  equipmentGroupName?: BilingualName | null;
  /** 通貨ID */
  currencyId?: string | null;
  /** 通貨名 */
  currencyName?: string | null;
  /** プロセスID */
  processId?: string | null;
  /** 表示順序 */
  displayOrder?: number | null;

  // その他
  /** 読み取り専用項目 */
  isReadonly?: boolean;

  /** データ連携時に初期値に変換されたフィールドとエラーメッセージのオブジェクト */
  invalidFields?: { [fieldName: string]: string };
}

/**
 * 設備投資情報 拡張
 */
export interface EquipmentExtType extends EquipmentType {
  /** 選択 */
  selected: boolean,
  /** 数量（増減数） */
  changeInQuantity: number,
  /** 検収月（変更後） - 段階的設備投資用 */
  changedAcceptanceMonth?: string,
  /** 償却開始月（変更後） - 段階的設備投資用 */
  changedDepreciationStartMonth?: string,
}

/**
 * シミュレーション設定用設備情報（保存用）
 */
export interface SavedEquipmentConfig {
  /** 設備ID */
  equipmentId: string;
  /** 数量（増減数） */
  changeInQuantity: number;
  /** 検収月（変更後） - 段階的設備投資用 */
  changedAcceptanceMonth?: string;
  /** 償却開始月（変更後） - 段階的設備投資用 */
  changedDepreciationStartMonth?: string;
}

/**
 * ノードデータ
 */
export interface NodeDataType {
  /** プロセス情報 */
  process: UniqueProcessType;
  /** プロセスに紐づく設備情報 */
  equipmentData?: EquipmentType[] | null;
  /** ノード押下処理 */
  onClick?: (data: any) => {};
  /** 接続点（左側）表示有無 */
  left: boolean,
  /** 接続点（右側）表示有無 */
  right: boolean,
  /** パーツNo/サイクルタイム文字色 */
  colors: { [partNo: string]: { color: string } },
  /** displayPartNo表示解決用のPartNo一覧 */
  partNoList?: Pick<PartNo, "partNo" | "displayPartNo">[] | null,
}

/**
 * CBOMファイルデータ+パーツNo
 */
export interface CBomWithPartNoType {
  /** パーツNo */
  partNo: string,
  /** CBOMデータ */
  cbomData: CBomType[],
  /** サイクルタイム単位換算（CycleTimeUnits.code） */
  cycleTimeUnits: string;
}
/**
 * CBOMファイル
 */
export interface CBomType {
  /** 連番 */
  seq?: number | null,
  /** レベル（工程グルーピング識別子） */
  level?: string | null,
  /** モデルNo */
  modelNo?: string | null,
  /** プロセスコード（工程コード） */
  processCode?: string | null,
  /** アイテムNo（プロセス名/工程名） */
  itemNo?: string | null,
  /** アイテム名 */
  itemName?: string | null,
  /**  */
  usage?: string | null,
  /** 単価 */
  unitPrice?: number | null,
  /** 金額 */
  amount?: number | null,
  /** ユニットタイム */
  unitTime?: string | null,
}

/**
 * 稼働日数情報
 */
export interface WorkingDaysType {
  /** 工場稼働日数（カレンダー上の稼働日数） */
  factoryWorkingDays: number[];
  /** 稼働日数 */
  workingDays: number[];
  /** 休日出勤日数(祝日出勤日数を含めた日数) */
  workOnWeekendAndHolidays: number[];
  /** ライン数 */
  lineCount: number[];
  /** 祝日出勤日数 */
  workOnHolidays: number[];
  /** 残業有無 */
  requiredOverTimes: boolean[],

  /** 年間合計工場稼働日数 */
  sumFactoryWorkingDays: number;
  /** 年間合計稼働日数 */
  sumWorkingDays: number;
  /** 年間合計休日出勤日数 */
  sumWeekendWorkDays: number;
  /** 年間合計祝日出勤日数 */
  sumHolidayWorkDays: number;
}

/**
 * 非稼働時間情報
 */
export interface NonWorkingHoursType {
  /** 標準労働時間 */
  workTime?: string;

  /** 残業時間 */
  overTime?: string;

  /** 勤務体制 */
  shiftSystem?: string;

  /** 総労働時間 */
  workingHours?: string;

  /** 非稼働項目 */
  nonWorkingItems?: NonWorkingType[];

  /** 稼働時間 */
  operatingHour?: string;

  /** 非稼働時間 */
  nonWorkingHour?: string;

  /** 稼働率 */
  availabilityRate?: number;

  /** 非稼働率 */
  unavailabilityRate?: number;
}

/**
 * 非稼働項目
 */
export interface NonWorkingType {
  /** 非稼働項目ID */
  nonWorkingId: string;

  /** 非稼働時間 */
  nonWorkingHour: string;
}

/**
 * 確認担当者情報
 */
export interface ConfirmorInfo {
  /** 担当者選択で選択された担当者であるかどうか */
  isConfirmor: boolean,
  /** 確認担当者名 */
  employeeName: string,
  /** 確認日時 */
  confirmTime?: string,
}

/** （sf移植） */
export interface IdNameInfo {
  id: string
  name: string
  deleted: boolean
}
/** （sf移植） */
export interface OpportunityInfo {
  id: string;
  name: string;
  imageFileName: string;
  massProduction?: string | null | undefined;
  deleted: boolean;
}

/** エラー情報定義 */
export interface Errors {
  [key: string]: Error,
}

/** エラー情報定義 */
export interface Error {
  isError: boolean,
  errorMessage: string
}

/**
 * 見積チェックリスト
 */
export interface EstimateCheckListFormType {

  /** 設備種類 */
  equipmentType: {
    /** 設備種類 */
    equipmentType: string,
  },

  /** リバースオークション有無 */
  reverseAuction: {
    /** リバースオークション有無 */
    reverseAuction: string,
  },

  /** 内製検討 */
  inHouseReview: {
    /** 内製検討 */
    inHouseReview: string,
    /** 内製検討なしの理由 */
    inHouseReviewNoReason: string | null,
  },

  /** 相見積 */
  competitiveEstimate: {
    /** 相見積 */
    competitiveEstimate: string,
    /** 相見積なしの理由 */
    competitiveEstimateNoReason: string | null,
  },

  /** 過去実績 */
  pastPerformance: {
    /** 過去実績 */
    pastPerformance: string,
    /** 直近購入時期 */
    purchaseDate: string | null,
    /** 取得単価 */
    purchasePrice: number | null,
  },

  /** 発注先 */
  supplier: {
    /** 発注先 */
    supplier: string,
    /** 商社・代理店の理由 */
    distributorReason: string | null,
    /** 発注時期 */
    orderDate: string | null,
  },

  /** 単価交渉 */
  negotiation: {
    /** 単価交渉の回数 */
    negotiationCount: number | null,
    /** 単価交渉の経緯 */
    negotiationBackground: string | null,
  },

  /** 支払条件 */
  paymentType: {
    /** 前払いの有無 */
    advancePayment: string,
    /** 支払条件 */
    paymentBackground: string | null,
    /** 調達物流部門担当役員の承認 */
    procurementApproval?: string | null,
  },

  /** 当社製品使用の確認/要請 */
  ourProductUsage?: {
    /** 使用状況 */
    usageStatus: string,
    /** 使用製品名 */
    productName: string | null,
  },

  /** 外注を使用せず購入する理由 */
  purchaseWithoutOutsourcing?: {
    /** 理由 */
    reason: string | null,
  },

  /** 相見積による比較 */
  equipmentComparison: {
    /** 相見積による比較 */
    equipmentComparison: EquipmentComparison[],
    /** 優位性 */
    superiority: string[] | null,
  },

  /** 選定理由 */
  selectReason: {
    /** 選定理由 */
    selectReason: string | null,
  },

  /** 通貨 */
  currency?: {
    /** 通貨ID */
    currencyId: string | null,
    /** 通貨名 */
    currencyName: string | null,
    /** 為替レート */
    exchangeRate: number | null,
  },

};

/** 製品カテゴリ（全階層対応） */
export interface ProductCategoryType {
  /** 製品カテゴリID */
  productCategoryId: string;
  /** 製品カテゴリ名 */
  productCategoryName: string;
  /** 階層レベル（1:大分類, 2:中分類, 3:小分類, 4:細分類） */
  hierarchy?: number;
  /** 上位カテゴリID */
  upperLevelCategoryId?: string;
  /** コメント */
  comment?: string;
  /** 優先度 */
  priority?: number;
  /** 削除フラグ */
  deleted?: boolean;
}

/**
 * 相見積による比較
 */
export interface EquipmentComparison {

  // 項目名
  companyName: string;
  // 本体価格
  basePrice: string;
  // 台数
  unit: string;
  // オプション価格
  optionPrice: string;
  // 特徴
  features: string;
  // 総金額
  totalPrice: string;
  // 納期
  deadline: string;
  // 精度/品質
  quality: string;
  // サービス体制
  system: string;
  // リピート
  repeat: string;
  // 課題
  issue: string;

}

// -----------------------------
// JSONデータ
// -----------------------------
// 1年度毎に管理するデータ
export interface YearData {
  quantity: number;   // 年度の数量合計(将来削除)
  //price: number;
  unitPrice: number;  // 年度の単価(加重平均)
  salesPrice: number; // 年度の売上高合計(将来削除)
  months: { [key: number]: MonthData };
}

export interface YearPriceData {
  price: number; // 年度の金額合計(将来削除)
  months: { [key: number]: MonthPriceData };
}

// 1か月毎に管理するデータ
export interface MonthData {
  month: number;
  quantity: number;
  price: number;
}

export interface MonthPriceData {
  month: number;
  price: number;  // 月度の金額
}

// QuantityAndPrice
export interface QuantityAndPriceMonthTable {
  id: string;
  partNo: string;
  years: number[];
  data: { [key: number]: YearData };
  //data: { [key: number]: { year: number; month:number; quantity: number; price: number } };
  moldRadioValue: string;
  developmentRadioValue: string;
  partNoMoldTables: MoldMonthTable[];
  partNoDevelopmentCostTables: DevelopmentMonthCostTable[];
  updateDate?: string | null,
  currencyAndRatio: { id: string; ratio: number }[];
  /** 新製品比率（0-100%）、既存製品比率は100-newProductRatioで算出 */
  newProductRatio?: number | null;
  /** セット販売での単価・数量入力フラグ（true: セット販売として入力、RDS r_ceas_part_no.is_set_sales_input に保存） */
  isSetSalesInput?: boolean;
}
export interface QuantityAndPriceTable {
  id: string;
  partNo: string;
  years: number[];
  data: { [key: number]: { quantity: number; price: number } };
  currencyAndRatio: { id: string; ratio: number };
  moldRadioValue: string;
  developmentRadioValue: string;
  partNoMoldTables: MoldTable[];
  partNoDevelopmentCostTables: DevelopmentCostTable[];
  updateDate?: string | null,
  /** 新製品比率（0-100%）、既存製品比率は100-newProductRatioで算出 */
  newProductRatio?: number | null;
}
// MoldSalesByProject, MoldSalesByPartNumber
export interface MoldTable {
  id: string;
  partNo: string;
  moldName: string;
  years: number[];
  data: { [key: number]: { price: number } };
}
export interface MoldMonthTable {
  id: string;
  partNo: string;
  moldName: string;
  years: number[];
  data: { [key: number]: YearPriceData };
}

export interface MoldMonthTableByProject {
  id: string;
  partNo: string;
  moldName: string;
  years: number[];
  data: { [key: number]: YearPriceData };
  currencyId: string;
}

// DevelopmentSalesByProject, DevelopmentSalesByPartNo
export interface DevelopmentCostTable {
  id: string;
  partNo: string;
  developmentCostName: string;
  years: number[];
  data: { [key: number]: { price: number } };
}

export interface DevelopmentMonthCostTable {
  id: string;
  partNo: string;
  developmentCostName: string;
  years: number[];
  data: { [key: number]: YearPriceData };
}

export interface DevelopmentMonthCostTableByProject {
  id: string;
  partNo: string;
  developmentCostName: string;
  years: number[];
  data: { [key: number]: YearPriceData };
  currencyId: string;
}
export interface EquipmentCostTable {
  id: string;
  partNo: string;
  equipmentCostName: string;
  years: number[];
  data: { [key: number]: { price: number } };
}

export interface EquipmentMonthCostTable {
  id: string;
  partNo: string;
  equipmentCostName: string;
  years: number[];
  data: { [key: number]: YearPriceData };
}

export interface EquipmentMonthCostTableByProject {
  id: string;
  partNo: string;
  equipmentCostName: string;
  years: number[];
  data: { [key: number]: YearPriceData };
  currencyId: string;
}

export interface PriceRows {
  id: number;
  displayStartMonth: string;
  displayEndMonth: string;
  startMonth: string;
  endMonth: string;
  value: number;
}

// customerSchedule
export interface ScheduleItem {
  id: string;
  name: string;
  date: string; // "YYYY-MM-DD"
  inputMode?: "select" | "input";
  source?: "sas";
}

// equipmentInstallationSchedule
export interface FacilityItem {
  id: string;
  name: string;
  startDate: string; // "YYYY-MM-DD"
  endDate: string;   // "YYYY-MM-DD"
  inputMode?: "select" | "input";
}

// reviewSchedule（振り返りスケジュール）
export interface ReviewScheduleItem {
  id: string;
  date: string;   // "YYYY-MM-DD"
  phase: string;  // "初期確認" | "標準" | "中期" | "最終" or 自由入力
  inputMode?: "select" | "input";
  isReviewed?: boolean; // 振り返り済みフラグ（r_ceas_reviewに登録済み、UI制御用・DB保存対象外）
}

/** 対象PJの損益試算画面（年度別） */
export interface OriginalProfitAndLossTable {
  /** 年度別損益 */
  profitAndLossByYear: {
    /** 売上合計 */
    totalSales: string[];
    /** 製品売上 */
    productSales: string[];
    /** 金型・設備費回収 */
    moldSales: string[];
    /** 開発費回収 */
    devSales: string[];
    /** 設備費回収 */
    equipmentSales: string[];
    /** 売上原価 */
    salesCost: string[];
    /** 材料費 */
    totalMaterialCost: string[];
    /** 外注加工費 */
    outsideCost: string[];
    /** 生産工場労務費 */
    laborCost: string[];
    /** 国内工場労務費 */
    domesticLaborCost: string[];
    /** 生産工場経費 */
    factoryExpenses: string[];
    /** 生産工場経費-償却費以外の工場経費 */
    overHeadCost: string[];
    /** 生産工場経費-減価償却費 */
    depreciation: string[];
    /** 生産工場経費-投資対象設備 */
    equipmentDepreciation: string[];
    /** 生産工場経費-工場建物 */
    factoryCost: string[];
    /** 生産工場経費-過去PJの投資対象設備 */
    pastEquipmentCost: string[];
    /** 生産工場経費-その他の設備 */
    otherEquipmentCost: string[];
    /** 国内工場経費 */
    domesticFactoryExpenses: string[];
    /** 国内工場経費-償却費以外の工場経費 */
    domesticOverHeadCost: string[];
    /** 国内工場経費-減価償却費 */
    domesticDepreciation: string[];
    /** 国内工場経費-投資対象設備 */
    domesticInvestmentDepreciation: string[];
    /** 国内工場経費-工場建物 */
    domesticFactoryBuildingDepreciation: string[];
    /** 国内工場経費-その他の設備 */
    domesticOtherDepreciation: string[];
    /** 仕損費 */
    scrapCost: string[];
    /** 販売費及び一般管理費 */
    sGAExpenses: string[];
    /** 生産工場販管費 */
    appliedLaborCost: string[];
    /** 国内工場販管費 */
    domesticSgaCost: string[];
    /** 本社・販社販管費 */
    salesManagementCost: string[];
    /** 開発費 */
    devCost: string[];
    /** ロイヤリティ */
    royalty: string[];
    /** その他の経費 */
    otherCost: string[];
    /** 営業利益 */
    profit: string[];
    /** 営業利益率 */
    profitRate: string[];
    /** 実質的営業利益率 */
    realOperatingProfitRate: string[];
    /** 実質的営業利益率の合計（オプション：後方互換性のため） */
    totalRealOperatingProfitRate?: string;
  };

  /** 回収期間 */
  paybackPeriodByYear: {
    /** 金利 */
    interestRateByYear: string[],
    /** 回収期間 */
    paybackPeriod: string,
  },

  /** 回収シミュレーション */
  RecoverySimulationByYear: {
    /** 設備投資額 */
    capexAmount: string[];
    /** 回収原資 */
    recoveredResources: string[];
    /** 営業利益 */
    profit: string[];
    /** 減価償却費 */
    depreciation: string[];
    /** 工場建物・その他設備の減価償却費 */
    factoryAndOtherEquipmentCost: string[];
    /** 未回収額 */
    uncollectedAmount: string[];
    /** 回収期間 */
    collectionPeriod: string;
  };
  /** ROIC */
  roicByYear: {
    /** 税引後営業利益 */
    nopatByYear: string[];
    /** 営業利益 */
    profit: string[];
    /** 目標税率（実効税率） */
    setTaxRate: string[];
    /** 投下資本 */
    undepreciatedByYear: string[];
    /** 累計設備投資額 */
    totalCapexAmount: string[];
    /** 投資対象設備の減価償却累計額 */
    equipmentDepreciation: string[];
    /** ROIC（簡易計算） */
    roicByYear: string[];
    /** ハードルレート */
    hurdleRate: string[];
  };
  /** DCF法 */
  dcf: {
    /** フリーキャッシュフロー */
    freeCashFlow: string[];
    /** 営業利益 */
    profit: string[];
    /** 目標税率（実効税率） */
    setTaxRate: string[];
    /** 減価償却費 */
    depreciation: string[];
    /** 設備投資額 */
    capexAmount: string[];
    /** 加重平均資本コスト */
    discountRate: string[];
    /** 現在価値 */
    presentValue: string[];
    /** 正味現在価値 */
    netPresentValue: string;
  };
}
/** 対象PJの損益試算画面（月別） */
export interface OriginalProfitAndLossByMonthTable {
  /** 年月ラベル */
  labels: string[];
  /** 売上合計 */
  totalSales: string[];
  /** 売上原価（製造原価） */
  salesCost: string[];
  /** 営業利益 */
  profit: string[];
  /** 数量（PartNo別） */
  quantityByPartNo: Record<string, string[]>;
}

/** 生産キャパグラフ：既存ラインPartNoの生産予定数量（1行分） */
export type TExistingLineQuantityRow = {
  /** PartNo（手入力。案件PartNoとの重複も許容） */
  partNo: string;
  /** 月別数量（キー: "YYYY/MM"、originalProfitAndLossByMonth.labels と同じ書式） */
  quantities: Record<string, number>;
};
/** 生産キャパグラフ：既存ラインPartNoの生産予定数量 */
export type TExistingLineQuantity = TExistingLineQuantityRow[];

/** 月次損益データ（Excel出力用） */
export interface MonthlyProfitAndLossData {
  /** 年月ラベル配列 */
  labels: string[];
  /** 売上合計 */
  totalSales: Decimal[];
  /** 製品売上 */
  productSales: Decimal[];
  /** 金型回収 */
  moldSales: Decimal[];
  /** 開発費回収 */
  devSales: Decimal[];
  /** 設備費回収 */
  equipmentSales: Decimal[];
  /** 売上原価 */
  salesCost: Decimal[];
  /** 材料費 */
  materialCost: Decimal[];
  /** 外注加工費 */
  outsideCost: Decimal[];
  /** 生産工場労務費 */
  laborCost: Decimal[];
  /** 国内工場労務費 */
  domesticLaborCost: Decimal[];
  /** 生産工場経費 */
  factoryExpenses: Decimal[];
  /** 生産工場経費-償却費以外の工場経費 */
  overHeadCost: Decimal[];
  /** 生産工場経費-減価償却費 */
  depreciation: Decimal[];
  /** 生産工場経費-投資対象設備 */
  equipmentDepreciation: Decimal[];
  /** 生産工場経費-過去PJの投資対象設備 */
  pastEquipmentCost?: Decimal[];
  /** 生産工場経費-工場建物 */
  factoryCost: Decimal[];
  /** 生産工場経費-その他の設備 */
  otherEquipmentCost: Decimal[];
  /** 国内工場経費 */
  domesticFactoryExpenses: Decimal[];
  /** 国内工場経費-償却費以外の工場経費 */
  domesticOverHeadCost?: Decimal[];
  /** 国内工場経費-減価償却費 */
  domesticDepreciation?: Decimal[];
  /** 国内工場経費-投資対象設備 */
  domesticInvestmentDepreciation?: Decimal[];
  /** 国内工場経費-工場建物 */
  domesticFactoryBuildingDepreciation?: Decimal[];
  /** 国内工場経費-その他の設備 */
  domesticOtherDepreciation?: Decimal[];
  /** 仕損費 */
  scrapCost: Decimal[];
  /** 販売費及び一般管理費 */
  sGAExpenses: Decimal[];
  /** 生産工場販管費 */
  appliedLaborCost: Decimal[];
  /** 国内工場販管費 */
  domesticSgaCost: Decimal[];
  /** 本社・販社販管費 */
  salesManagementCost: Decimal[];
  /** 開発費 */
  devCost: Decimal[];
  /** ロイヤリティ */
  royalty: Decimal[];
  /** その他の経費 */
  otherCost: Decimal[];
  /** 営業利益 */
  profit: Decimal[];
  /** 客先指定部品材料費（実質的営業利益率計算用） */
  customerSpecifiedPartsCost: Decimal[];
  /**
   * 表示期間外（ダウンサイドの生産期間短縮後）に残る減価償却費の合計（No.411対応）。
   * 減価償却費は生産終了後も発生し続けるが、月別配列（profit）は表示期間分しか持てないため、
   * 実質的営業利益率の分子（営業利益合計）へ加算するための残余分を別途保持する。
   * 期間短縮がない場合は0。
   */
  residualDepreciationTotal?: Decimal;
}

/**
 * 更新投資の損益試算データ（年度別）
 * 更新投資の場合のみ使用
 */
export interface UpdateInvestmentProfitLossData {
  /** 年度別損益（更新前） */
  beforeUpdateProfitAndLossByYear: {
    /** 売上合計 */
    totalSales: string[];
    /** 製品売上 */
    productSales: string[];
    /** 金型・設備費回収 */
    moldSales: string[];
    /** 開発費回収 */
    devSales: string[];
    /** 設備費回収 */
    equipmentSales: string[];
    /** 売上原価 */
    salesCost: string[];
    /** 材料費 */
    totalMaterialCost: string[];
    /** 外注加工費 */
    outsideCost: string[];
    /** 生産工場労務費 */
    laborCost: string[];
    /** 国内工場労務費 */
    domesticLaborCost: string[];
    /** 生産工場経費 */
    factoryExpenses: string[];
    /** 生産工場経費-償却費以外の工場経費 */
    overHeadCost: string[];
    /** 生産工場経費-減価償却費 */
    depreciation: string[];
    /** 生産工場経費-投資対象設備 */
    equipmentDepreciation: string[];
    /** 生産工場経費-工場建物 */
    factoryCost: string[];
    /** 生産工場経費-過去PJの投資対象設備 */
    pastEquipmentCost: string[];
    /** 生産工場経費-その他の設備 */
    otherEquipmentCost: string[];
    /** 国内工場経費 */
    domesticFactoryExpenses: string[];
    /** 国内工場経費-償却費以外の工場経費 */
    domesticOverHeadCost: string[];
    /** 国内工場経費-減価償却費 */
    domesticDepreciation: string[];
    /** 国内工場経費-投資対象設備 */
    domesticInvestmentDepreciation: string[];
    /** 国内工場経費-工場建物 */
    domesticFactoryBuildingDepreciation: string[];
    /** 国内工場経費-その他の設備 */
    domesticOtherDepreciation: string[];
    /** 仕損費 */
    scrapCost: string[];
    /** 販売費及び一般管理費 */
    sGAExpenses: string[];
    /** 生産工場販管費 */
    appliedLaborCost: string[];
    /** 国内工場販管費 */
    domesticSgaCost: string[];
    /** 本社・販社販管費 */
    salesManagementCost: string[];
    /** 開発費 */
    devCost: string[];
    /** ロイヤリティ */
    royalty: string[];
    /** その他の経費 */
    otherCost: string[];
    /** 営業利益 */
    profit: string[];
    /** 営業利益率 */
    profitRate: string[];
    /** 実質的営業利益率 */
    realOperatingProfitRate: string[];
    /** 実質的営業利益率の合計（オプション：後方互換性のため） */
    totalRealOperatingProfitRate?: string;
  };
  /** 更新による改善効果（工程別）- 詳細モード用 */
  improvementEffectByProcess?: UpdateImprovementEffectByProcess[];
  /** 改善額合計（差異の合計）- 詳細モード用 */
  improvementTotal?: {
    /** 仕損費 */
    scrapCost: string;
    /** 修繕費 */
    repairCost: string;
    /** 消耗品費 */
    consumablesCost: string;
    /** 合計 */
    total: string;
  };
  /** 更新による改善効果（指標）- 工程別 - 簡易モード用 */
  improvementEffectIndicatorByProcess: RationalizationImprovementEffectByProcess[];
  /** 更新による改善効果（費用）- 工程別 - 簡易モード用 */
  improvementEffectCostByProcess: RationalizationImprovementEffectByProcess[];
  /** 改善効果の合計（指標）- 簡易モード用 */
  improvementTotalIndicator: RationalizationImprovementTotalItem[];
  /** 改善効果の合計（費用）- 簡易モード用 */
  improvementTotalCost: RationalizationImprovementTotalItem[];
}

/**
 * 更新による改善効果（工程別）
 */
export interface UpdateImprovementEffectByProcess {
  /** 工程名 */
  processName: string;
  /** 歩留 */
  yield: {
    /** 直近3ヶ月平均実績 */
    actual: string;
    /** 立上時目論見 */
    initial: string;
    /** 目論見 */
    target: string;
    /** 実績 v 目論見 */
    difference: string;
  };
  /** 仕損費 */
  scrapCost: {
    /** 直近3ヶ月平均実績 */
    actual: string;
    /** 立上時目論見 */
    initial: string;
    /** 目論見 */
    target: string;
    /** 実績 v 目論見 */
    difference: string;
  };
  /** 修繕費 */
  repairCost: {
    /** 直近3ヶ月平均実績 */
    actual: string;
    /** 立上時目論見 */
    initial: string;
    /** 目論見 */
    target: string;
    /** 実績 v 目論見 */
    difference: string;
  };
  /** 消耗品費 */
  consumablesCost: {
    /** 直近3ヶ月平均実績 */
    actual: string;
    /** 立上時目論見 */
    initial: string;
    /** 目論見 */
    target: string;
    /** 実績 v 目論見 */
    difference: string;
  };
}

/**
 * 合理化投資の損益試算データ（年度別）
 * 合理化投資の場合のみ使用
 * ※金額は工場通貨（mmiSourceCurrencyId）に変換済みで保存
 */
export interface RationalizationInvestmentProfitLossData {
  /** 年度ラベル（例：["2025年3月期", "2026年3月期"]） */
  fiscalYears: string[];
  /** 年度別損益（合理化前） */
  beforeProfitAndLossByYear?: RationalizationProfitAndLossByYear;
  /** 年度別損益（合理化後） */
  afterProfitAndLossByYear?: RationalizationProfitAndLossByYear;
  /** 合理化による改善効果（指標）- 工程別 */
  improvementEffectIndicatorByProcess: RationalizationImprovementEffectByProcess[];
  /** 合理化による改善効果（費用）- 工程別 */
  improvementEffectCostByProcess: RationalizationImprovementEffectByProcess[];
  /** 改善効果の合計（指標） */
  improvementTotalIndicator: RationalizationImprovementTotalItem[];
  /** 改善効果の合計（費用） */
  improvementTotalCost: RationalizationImprovementTotalItem[];
}

/**
 * 合理化投資の年度別損益データ
 * ※金額は工場通貨（mmiSourceCurrencyId）に変換済みで保存
 */
export interface RationalizationProfitAndLossByYear {
  /** 売上合計 */
  totalSales: string[];
  /** 製品売上 */
  productSales: string[];
  /** 金型・設備費回収 */
  moldSales: string[];
  /** 開発費回収 */
  devSales: string[];
  /** 設備費回収 */
  equipmentSales: string[];
  /** 売上原価 */
  salesCost: string[];
  /** 材料費 */
  totalMaterialCost: string[];
  /** 外注加工費 */
  outsideCost: string[];
  /** 生産工場労務費 */
  laborCost: string[];
  /** 国内工場労務費 */
  domesticLaborCost: string[];
  /** 生産工場経費 */
  factoryExpenses: string[];
  /** 生産工場経費-償却費以外の工場経費 */
  overHeadCost: string[];
  /** 生産工場経費-減価償却費 */
  depreciation: string[];
  /** 生産工場経費-投資対象設備 */
  equipmentDepreciation: string[];
  /** 生産工場経費-工場建物 */
  factoryCost: string[];
  /** 生産工場経費-過去PJの投資対象設備 */
  pastEquipmentCost: string[];
  /** 生産工場経費-その他の設備 */
  otherEquipmentCost: string[];
  /** 国内工場経費 */
  domesticFactoryExpenses: string[];
  /** 国内工場経費-償却費以外の工場経費 */
  domesticOverHeadCost: string[];
  /** 国内工場経費-減価償却費 */
  domesticDepreciation: string[];
  /** 国内工場経費-投資対象設備 */
  domesticInvestmentDepreciation: string[];
  /** 国内工場経費-工場建物 */
  domesticFactoryBuildingDepreciation: string[];
  /** 国内工場経費-その他の設備 */
  domesticOtherDepreciation: string[];
  /** 仕損費 */
  scrapCost: string[];
  /** 販売費及び一般管理費 */
  sGAExpenses: string[];
  /** 生産工場販管費 */
  appliedLaborCost: string[];
  /** 国内工場販管費 */
  domesticSgaCost: string[];
  /** 本社・販社販管費 */
  salesManagementCost: string[];
  /** 開発費 */
  devCost: string[];
  /** ロイヤリティ */
  royalty: string[];
  /** その他の経費 */
  otherCost: string[];
  /** 営業利益 */
  profit: string[];
  /** 営業利益率 */
  profitRate: string[];
  /** 実質的営業利益率 */
  realOperatingProfitRate: string[];
  /** 売上合計（合計） */
  totalSalesSum?: string;
  /** 製品売上（合計） */
  productSalesSum?: string;
  /** 金型・設備費回収（合計） */
  moldSalesSum?: string;
  /** 開発費回収（合計） */
  devSalesSum?: string;
  /** 設備費回収（合計） */
  equipmentSalesSum?: string;
  /** 売上原価（合計） */
  salesCostSum?: string;
  /** 材料費（合計） */
  totalMaterialCostSum?: string;
  /** 外注加工費（合計） */
  outsideCostSum?: string;
  /** 生産工場労務費（合計） */
  laborCostSum?: string;
  /** 国内工場労務費（合計） */
  domesticLaborCostSum?: string;
  /** 生産工場経費（合計） */
  factoryExpensesSum?: string;
  /** 生産工場経費-償却費以外の工場経費（合計） */
  overHeadCostSum?: string;
  /** 生産工場経費-減価償却費（合計） */
  depreciationSum?: string;
  /** 生産工場経費-投資対象設備（合計） */
  equipmentDepreciationSum?: string;
  /** 生産工場経費-工場建物（合計） */
  factoryCostSum?: string;
  /** 生産工場経費-過去PJの投資対象設備（合計） */
  pastEquipmentCostSum?: string;
  /** 生産工場経費-その他の設備（合計） */
  otherEquipmentCostSum?: string;
  /** 国内工場経費（合計） */
  domesticFactoryExpensesSum?: string;
  /** 国内工場経費-償却費以外の工場経費（合計） */
  domesticOverHeadCostSum?: string;
  /** 国内工場経費-減価償却費（合計） */
  domesticDepreciationSum?: string;
  /** 国内工場経費-投資対象設備（合計） */
  domesticInvestmentDepreciationSum?: string;
  /** 国内工場経費-工場建物（合計） */
  domesticFactoryBuildingDepreciationSum?: string;
  /** 国内工場経費-その他の設備（合計） */
  domesticOtherDepreciationSum?: string;
  /** 仕損費（合計） */
  scrapCostSum?: string;
  /** 販売費及び一般管理費（合計） */
  sGAExpensesSum?: string;
  /** 生産工場販管費（合計） */
  appliedLaborCostSum?: string;
  /** 国内工場販管費（合計） */
  domesticSgaCostSum?: string;
  /** 本社・販社販管費（合計） */
  salesManagementCostSum?: string;
  /** 開発費（合計） */
  devCostSum?: string;
  /** ロイヤリティ（合計） */
  royaltySum?: string;
  /** その他の経費（合計） */
  otherCostSum?: string;
  /** 営業利益（合計） */
  profitSum?: string;
  /** 営業利益率（合計） */
  profitRateSum?: string;
  /** 実質的営業利益率（合計） */
  realOperatingProfitRateSum?: string;
}

/**
 * 合理化による改善効果（工程別）
 */
export interface RationalizationImprovementEffectByProcess {
  /** 工程ID */
  processId: string;
  /** 工程名 */
  processName: string;
  /** 項目リスト */
  items: RationalizationImprovementEffectItem[];
}

/**
 * 合理化による改善効果の項目
 */
export interface RationalizationImprovementEffectItem {
  /** 項目種類（productionQuantity, laborCost, custom等） */
  itemType: string;
  /** 項目名（表示用） */
  itemName: string;
  /** 合理化前の年度別データ */
  beforeByYear: string[];
  /** 合理化前の合計 */
  beforeTotal: string;
  /** 合理化後の年度別データ */
  afterByYear: string[];
  /** 合理化後の合計 */
  afterTotal: string;
}

/**
 * 改善効果の合計の項目
 */
export interface RationalizationImprovementTotalItem {
  /** 項目種類 */
  itemType: string;
  /** 項目名（表示用） */
  itemName: string;
  /** 差異の年度別データ（合理化後 - 合理化前） */
  differenceByYear: string[];
  /** 差異の合計 */
  differenceTotal: string;
}

/**
 * 年の値保持用
 */
export interface MonthValue {
  /** 年 */
  year: number,
  /** 月 */
  month: number,
  /** 値 */
  value: any,
}

/**
 * 月別費用データ
 */
export interface MonthlyExpenseData {
  /** 年 */
  year: number;
  /** 月 */
  month: number;
  /** 外注加工費 */
  outsourcingProcessingCost: number;
  /** 生産工場労務費 */
  productionLaborCost: number;
  /** 国内工場労務費 */
  domesticLaborCost: number;
  /** 償却費以外の工場経費（生産） */
  overseasFactoryExpenses: number;
  /** 減価償却費-工場建物（生産） */
  factoryBuildingDepreciation: number;
  /** 減価償却費-過去PJの投資対象設備（生産） */
  pastEquipmentCost?: number;
  /** 減価償却費-その他の設備（生産） */
  otherEquipmentCost?: number;
  /** 償却費以外の工場経費（国内） */
  domesticFactoryExpenses: number;
  /** 減価償却費-投資対象設備（国内） */
  domesticInvestmentDepreciation?: number;
  /** 減価償却費-工場建物（国内） */
  domesticFactoryBuildingDepreciation?: number;
  /** 減価償却費-その他の設備（国内） */
  domesticOtherDepreciation?: number;
  /** 生産工場販管費 */
  productionSgaExpenses: number;
  /** 国内工場販管費 */
  domesticSgaExpenses: number;
  /** 販社販管費 */
  salesCompanySga: number;
  /** 開発費 */
  devCost?: number;
  /** ロイヤリティ */
  royalty: number;
  /** その他の経費 */
  otherCost?: number;
}

/**
 * プロジェクト追加情報
 */
export interface ProjectAdditionalInfo {
  /** プロジェクトID */
  projectId?: string;
  /** ベンチマークデータ */
  benchmarkData?: string;
  /** 更新前後サマリーデータ */
  updateSummaryData?: string;
  /** 合理化前後サマリーデータ */
  rationalizationSummaryData?: string;
  /** 汎用設備チェックリストデータ */
  generalPurposeChecklistData?: string;
  /** 更新日時 */
  updateDate?: string;
}

/**
 * 大容量チャートデータ（RDS: r_ceas_project_additional_info）
 */
export interface LargeChartData {
  /** プロジェクトID */
  projectId?: string;
  /** 合理化前後サマリーチャートデータ */
  rationalizationSummaryChartData?: string;
  /** シミュレーションチャートデータ（全チャートフィールドのJSON） */
  simulationChartData?: string;
}

/**
 * ベンチマーク状態データ（ベンチマーク入力用）
 */
export interface BenchmarkStateData {
  /** 歩留率 (%) */
  yield?: number;
  /** 稼働率 (%) */
  operatingRate?: number;
  /** サイクルタイム (秒) */
  cycleTime?: number;
  /** 1日あたり必要人員数 */
  personnelPerDay?: number;
  /** 生産数 (個) */
  productionVolume?: number;
  /** 設備当たり生産性 (個/時間) */
  productionPerEquipment?: number;
  /** 1人あたり生産性 (個/時間) */
  productionPerPerson?: number;
}

/**
 * PartNo別ベンチマークデータ
 */
export interface BenchmarkDataByPartNo {
  /** 実績ベスト値 */
  actualBest: BenchmarkStateData;
  /** 理論値 */
  theoretical: BenchmarkStateData;
  /** 投資計画値 */
  investmentPlan: BenchmarkStateData;
}

/**
 * 生産能力ベンチマークデータ(仮)
 */
export interface ProductionCapacityBenchmark {
  /** 入力タイプ: 設備単位 or ライン単位 */
  inputType: "equipment" | "line";
  /** 設備ID (設備入力の場合) */
  equipmentId?: string;
  /** 設備名 (設備入力の場合) */
  equipmentName?: string;
  /** 生産ラインID (ライン入力の場合) */
  productionLineId?: string;
  /** 生産ライン名 (ライン入力の場合) */
  productionLineName?: string;
  /** ベンチマーク月 (YYYY-MM形式) */
  benchmarkMonth: string;
  /** BOM */
  processCode: string;
  /** 固定資産番号 */
  fixedAssetNumber: string;
  /** 稼働日数 */
  operatingDays: number;
  /** 実績ベスト値 */
  actualBest: BenchmarkStateData;
  /** 理論値 */
  theoretical: BenchmarkStateData;
  /** 投資計画値 */
  investmentPlan: BenchmarkStateData;
  /** ギャップ分析 */
  gapAnalysis: string;
  /** 設備あたりの生産性向上対策 */
  productionPerEquipment: string;
  /** 一人あたりの生産性向上対策 */
  productionPerPerson: string;
  /** 非稼働対策 */
  nonOperationMeasures: string;
  /** 歩留向上対策 */
  yieldMeasures: string;
  /** 自由入力項目 (key: フィールド名, value: 入力値) */
  freeInputFields: { [key: string]: string };
  // 標準労働時間
  workTime: string,
  // 勤務体制
  shiftSystem: string,
  /** 選択中のPartNo（ドロップダウンの選択値） */
  selectedPartNo?: string;
  /** PartNoごとのベンチマークデータ */
  benchmarkByPartNo?: { [partNo: string]: BenchmarkDataByPartNo };
}

/**
 * 更新前後サマリーデータ
 */
export interface UpdateSummaryInputData {
  /** 生産ラインID（工程ID） */
  productionLineId?: string;
  /** 生産ライン名（工程名） */
  productionLineName?: string;
  /** 固定資産番号 */
  fixedAssetNumber: string;
  /** 稼働日数 */
  operatingDays: number;
  /** 労働時間 */
  workTime: string;
  /** 勤務体制 */
  shiftSystem: string;
  /** 部品数 */
  partsCount?: number;
  /** 当該工程までの累積原価 (JPY) */
  cumulativeCost?: number;
  /** 選択されたPartNo */
  selectedPartNo?: string;
  /** PartNoごとの生産能力データ */
  productionCapacityByPartNo?: { [partNo: string]: ProductionCapacityDataByPartNo };
  /** 更新をする理由 */
  updateReason: string;
  /** 設備あたりの生産性向上対策 */
  productionPerEquipment: string;
  /** 一人あたりの生産性向上対策 */
  productionPerPerson: string;
  /** 非稼働対策 */
  nonOperationMeasures: string;
  /** 非加工対策 */
  nonProcessingMeasures: string;
  /** 現行設備の処置方法 */
  currentEquipmentHandling: string;
  /** 損益データ */
  profitLoss?: ProfitLossData;
  /** 指標グループ項目（前後2軸表・合理化前後サマリーと同等） */
  indicatorItems?: RationalizationProfitLossItem[];
  /** 費用グループ項目（前後2軸表・合理化前後サマリーと同等） */
  costItems?: RationalizationProfitLossItem[];
  /** 費用の通貨ID（前後2軸表） */
  costCurrencyId?: string;
}

/**
 * 生産能力データ
 */
export interface ProductionCapacityDataByPartNo {
  /** 直近実績 */
  recentActual: ProductionCapacityData;
  /** 想定 */
  theoretical: ProductionCapacityData;
  /** 目論見 */
  investmentPlan: ProductionCapacityData;
}

/**
 * 生産能力データ
 */
export interface ProductionCapacityData {
  /** 歩留 (%) */
  yield?: number;
  /** 非稼働時間 (hr/24hr) */
  nonWorkingTime?: number;
  /** 機械トラブル (hr/24hr) */
  machineTrouble?: number;
  /** 保守・メンテナンス (hr/24hr) */
  maintenance?: number;
  /** 稼働率 (%) ※計算値 */
  operatingRate?: number;
  /** サイクルタイム (sec) */
  cycleTime?: number;
  /** 1日あたり必要人員数 (pax) */
  personnelPerDay?: number;
  /** 生産能力 (pcs) ※計算値 */
  productionVolume?: number;
  /** 設備あたり生産性 (pcs) ※計算値 */
  productionPerEquipment?: number;
  /** 1人あたり生産性 (pcs) ※計算値 */
  productionPerPerson?: number;
}

/**
 * 損益データ
 */
export interface ProfitLossData {
  /** 生産数 (pcs) */
  productionQuantity?: number;
  /** 直近実績 */
  recentActual?: ProfitLossStateData;
  /** 想定 */
  theoretical?: ProfitLossStateData;
  /** 目論見 */
  investmentPlan?: ProfitLossStateData;
}

/**
 * 損益データ
 */
export interface ProfitLossStateData {
  /** スクラップ率 (%) */
  scrapRate?: number;
  /** スクラップ費用 (JPY) */
  scrapCost?: number;
  /** 修繕費 (JPY) */
  repairCost?: number | null;
  /** 消耗品費 (JPY) */
  consumablesCost?: number | null;
}

/**
 * T_CEAS_SCENARIO（シナリオ情報）
 */
export interface TScenario {
  /** プロジェクトID */
  projectId: string;
  /** シナリオID */
  scenarioId: string;
  /** シナリオ名 */
  scenarioName: string;
  /** 稼働日数 */
  workingDays: WorkingDaysScenario;
  /** 稼働率 */
  nonWorkingHours: NonWorkingHoursScenario;
  /** 歩留 */
  yieldRate: YieldRateScenario;
  /** サイクルタイム */
  cycleTime: CycleTimeScenario;
  /** 設備投資 */
  equipment: EquipmentsScenario;
  /** 数量単価 */
  quantityAndPrice: QuantityAndPriceScenario;
  /** 労務費（生産工場労務費・償却費以外の工場経費） */
  laborCost?: LaborCostScenario;
  /** ストレス反映フラグ */
  applyDownside?: boolean;
  /** 更新日時 */
  updateDate: string;
}

/**
 * シナリオ設定（労務費）
 */
export interface LaborCostScenario {
  /** 生産工場労務費（月別） */
  productionLaborCostByMonth: number[];
  /** 償却費以外の工場経費（月別） */
  overseasFactoryExpensesByMonth: number[];
  /** 生産工場労務費単価（費用画面の値を初期値としたシミュレーション設定値） */
  productionFactoryLaborUnitPrice?: number | null;
  /** 生産工場経費単価（費用画面の値を初期値としたシミュレーション設定値） */
  productionFactoryExpenseUnitPrice?: number | null;
  /** 労務費ベースアップ率（費用画面の値を初期値としたシミュレーション設定値） */
  baseLaborCostIncreaseRate?: number | null;
}

/**
 * シナリオ設定（稼働日数）
 */
export interface WorkingDaysScenario {
  /** 稼働日数 */
  workingDay: { fiscalYear: string, workingDay: number[] }[];
  /** 休日 */
  holiday: { fiscalYear: string, holiday: number[] }[];
  /** 稼働日数データ */
  workingDaysData: { fiscalYear: string, workingDaysData: WorkingDaysType }[];
}

/**
 * シナリオ設定（非稼働時間）
 */
export interface NonWorkingHoursScenario {
  /** 残業時間 */
  overTime: { fiscalYear: string, overTime: boolean[] }[];
  /** 稼働率 */
  availabilityRate: { fiscalYear: string, availabilityRate: number[] }[];
  /** 非稼働時間データ */
  nonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[];
}

/**
 * シナリオ設定（歩留）
 */
export interface YieldRateScenario {
  /** 歩留データ */
  yieldRateData: string;
}

/**
 * シナリオ設定（サイクルタイム）
 */
export interface CycleTimeScenario {
  /** サイクルタイムデータ */
  cycleTimeData: string;
}

/**
 * シナリオ設定（設備投資）
 */
export interface EquipmentsScenario {
  /** 設備投資データ */
  equipmentsData: string;
}

/**
 * シナリオ設定（数量・単価）
 */
export interface QuantityAndPriceScenario {
  /** 数量・単価データ */
  quantityAndPriceData: string;
}

/**
 * ストレス指数
 */
export interface DownsideIndex {

  /** 数量経時変化 */
  quantityChangeOverTime: number[],

  /** 数量上下変動 */
  quantityFluctuation: number[],

  /** 単価経時変化 */
  priceChangeOverTime: number[],

  /** 顧客製品寿命 */
  customerProductLifecycle: number,

  /** 為替変動（単価） */
  exchangeRateFluctuationToPrice: number[],

  /** 為替変動（原価） */
  exchangeRateFluctuationToCost: number[],

  /** 材料高騰 */
  materialCostIncrease: number[],

  /** 労務費高騰 */
  laborCostIncrease: number[],

  /** 競合状態（数量） */
  competitorStatusToQuantity: number[],

  /** 競合状態（単価） */
  competitorStatusToPrice: number[],

}

/**
 * 価格と材料単価
 */
export interface MergedChartData {
  // 当初計画 - 単価
  monthlyPriceArrayOriginal: (string | null)[];
  // 当初計画 - 材料費の単価（内部調整単価）
  monthlyMaterialArrayOriginal: (string | null)[];

  // ダウンサイド - 単価
  monthlyPriceArrayModified: (string | null)[];
  // ダウンサイド - 材料費の単価（内部調整単価）
  monthlyMaterialArrayModified: (string | null)[];
}

/**
 * 歩留改善項目
 */
export interface YieldRateImprovement {
  /** 適用開始年月 (YYYY-MM形式) */
  month: string;
  /** 改善後歩留 (0-1の範囲) */
  yieldRate: number;
  /** 改善後歩留テキスト (画面表示用) */
  yieldRateText: string;
}

/**
 * サイクルタイム改善項目
 */
export interface CycleTimeImprovement {
  /** 適用開始年月 (YYYY-MM形式) */
  month: string;
  /** 改善後サイクルタイム (秒) */
  cycleTime: number;
  /** 改善後サイクルタイムテキスト (画面表示用) */
  cycleTimeText: string;
}

/**
 * 保存されたシミュレーション設定
 */
export interface SavedSimulationConfig {
  /** 設定ID（一意の識別子） */
  id: string;
  /** 設定名 */
  name: string;
  /** 更新日時 */
  updateDate: string;

  /** 改善された稼働日数（年別） */
  improvedWorkingDayByYear: { fiscalYear: string, workingDay: number[] }[];
  /** 改善されたライン数（年別） */
  improvedLineCountByYear: { fiscalYear: string, lineCount: number[] }[];
  /** 改善された休日（年別） */
  improvedHolidayByYear: { fiscalYear: string, holiday: number[] }[];
  /** 改善された残業時間（年別） */
  improvedOverTimeByYear: { fiscalYear: string, overTime: boolean[] }[];
  /** 改善された稼働率（月別） */
  improvedAvailabilityRateByMonth: { fiscalYear: string, availabilityRate: number[] }[];
  /** 改善された歩留率リスト */
  improvedYieldRateList: YieldRateImprovement[];
  /** 改善されたサイクルタイムリスト */
  improvedCycleTimeList: CycleTimeImprovement[];
  /** 改善された稼働日数データ */
  improvedWorkingDaysData: { fiscalYear: string, workingDaysData: WorkingDaysType }[];
  /** 改善された非稼働時間データ */
  improvedNonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[];
  /** 設備・人員構成変更データ */
  equipmentAmounts: SavedEquipmentConfig[];
  /** 数量・単価変更データ */
  quantityAndPrice: QuantityAndPriceMonthTable[];
  /** 改善された生産工場労務費（月別） */
  scenarioLaborCostByMonth?: number[];
  /** 改善された償却費以外の工場経費（月別） */
  scenarioOverseasFactoryExpensesByMonth?: number[];
  /** シミュレーション設定：生産工場労務費単価 */
  scenarioProductionFactoryLaborUnitPrice?: number | null;
  /** シミュレーション設定：生産工場経費単価 */
  scenarioProductionFactoryExpenseUnitPrice?: number | null;
  /** シミュレーション設定：労務費ベースアップ率 */
  scenarioBaseLaborCostIncreaseRate?: number | null;
  /** ストレス反映フラグ */
  applyDownside?: boolean;
}

/**
 * 売上（パーツNo別）
 */
export interface SalesByPartNo {
  [partNo: string]: Decimal[]
};

/**
 * １０カ条チェックリストデータ
 */
export interface CheckListDataMap {
  [equipmentId: string]: {
    checkList: any;
    updateDate: string;
  };
}

// ============================================
// BOM関連型定義
// ============================================

/**
 * 共通列の行データ
 */
export interface ProcessBomCommonRow {
  /** Seq. */
  seq: string;
  /** Level */
  level: string;
  /** Model No. */
  modelNo: string;
  /** Process Code */
  processCode: string;
  /** Item No. */
  itemNo: string;
  /** Item Name */
  itemName: string;
}

/**
 * ProcessBOM(initial)シートの固定列データ
 */
export interface ProcessBomInitialRow extends ProcessBomCommonRow {
  /** 材料費行 */
  isMaterialCostRow: boolean,

  /** 客先指定部品 */
  isCustomerSpecifiedParts: boolean;
}

/**
 * ProcessBOM(initial)シートデータ
 */
export interface ProcessBomInitialSheetData {
  /** データ行 */
  dataRows: ProcessBomInitialRow[];
}

/**
 * MaterialCostシートの行データ
 */
export interface MaterialCostRow extends ProcessBomCommonRow {
  /** Usage（使用量） */
  usage?: string;

  /** 購入通貨ID（材料費行の場合のみ設定、プロセス行の場合はnull） */
  purchaseCurrencyId?: string | null;

  /** 年月別データ - U/P (Combined)（単価（複合））- キーは "YYYY-MM" 形式 */
  unitPriceCombinedMonthly: Record<string, string>;

  /** 年月別データ - U/P(inhouse)（内製単価）- キーは "YYYY-MM" 形式 */
  unitPriceInhouseMonthly: Record<string, string>;

  /** 年月別データ - U/P(external)（外製単価）- キーは "YYYY-MM" 形式 */
  unitPriceExternalMonthly: Record<string, string>;

  /** 年月別データ - InhouseRatio（内製比率）- キーは "YYYY-MM" 形式 */
  inhouseRatioMonthly: Record<string, string>;

  /** 年月別データ - MaterialYield（材料歩留まり）- キーは "YYYY-MM" 形式 */
  materialYieldMonthly: Record<string, string>;

  /** 年月別データ - Amount（金額）- キーは "YYYY-MM" 形式 */
  amountMonthly: Record<string, string>;
}

/**
 * MaterialCostシートデータ
 */
export interface MaterialCostSheetData {
  /** データ行 */
  dataRows: MaterialCostRow[];
}

/**
 * Cycletimeシートの行データ
 */
export interface CycletimeRow extends ProcessBomCommonRow {
  /** 年月別データ（動的列） - キーは "YYYY-MM" 形式 */
  monthlyData: Record<string, string>;
}

/**
 * Cycletimeシートデータ
 */
export interface CycletimeSheetData {
  /** 年月列のリスト（表示順） - "YYYY-MM" 形式 */
  // monthColumns: string[];
  /** データ行 */
  dataRows: CycletimeRow[];
}

/**
 * Yieldシートの行データ
 */
export interface YieldRow extends ProcessBomCommonRow {
  /** 年月別データ（動的列） - キーは "YYYY-MM" 形式 */
  monthlyData: Record<string, string>;
}

/**
 * Yieldシートデータ
 */
export interface YieldSheetData {
  /** 年月列のリスト（表示順） - "YYYY-MM" 形式 */
  // monthColumns: string[];
  /** データ行 */
  dataRows: YieldRow[];
}

/**
 * 全シートデータの統合型
 */
export interface BomSheetsData {
  /** ProcessBOM(initial)シートデータ */
  processBom: ProcessBomInitialSheetData;

  /** MaterialCostシートデータ */
  materialCost: MaterialCostSheetData;

  /** Cycletimeシートデータ */
  cycletime: CycletimeSheetData;

  /** Yieldシートデータ */
  yield: YieldSheetData;
}

/**
 * BOM情報（DB保存用）
 */
export interface BomData {
  /** プロジェクトID */
  projectId?: string | null,
  /** パーツNo */
  partNo?: string | null,
  /** 工程順序 */
  seq?: string | null,
  /** レベル */
  level?: string | null,
  /** モデルNo. */
  modelNo?: string | null,
  /** プロセスID */
  processCode?: string | null,
  /** アイテムNo. */
  itemNo?: string | null,
  /** アイテム名 */
  itemName?: string | null,
  /** 使用量 */
  usage?: string | null,
  /** 客先指定部品 */
  isCustomerSpecifiedParts?: string | null,
  /** 購入通貨ID */
  purchaseCurrencyId?: string | null,
  /** 材料費 単価（複合）(年月管理) */
  unitPriceCombinedMonthly?: string | null,
  /** 材料費 内製単価(年月管理) */
  unitPriceInhouseMonthly?: string | null,
  /** 材料費 外注単価(年月管理) */
  unitPriceExternalMonthly?: string | null,
  /** 材料費 内製比率(年月管理) */
  inhouseRatioMonthly?: string | null,
  /** 材料費 歩留まり(年月管理) */
  materialYieldMonthly?: string | null,
  /** 金額(年月管理) */
  amountMonthly?: string | null,
  /** サイクルタイム(秒)(年月管理) */
  cycleTimeMonthly?: string | null,
  /** 歩留まり(年月管理) */
  yieldMonthly?: string | null,
  /** 更新日時 */
  updateDate?: string | null,
}

/**
 * 月別材料データ（BOMアイテム毎）
 */
export interface MonthlyMaterialData {
  /** 使用量 */
  Usage: number;
  /** 単価 */
  UnitPrice: number;
  /** 金額 */
  Amount: number;
  /** 内製利益を考慮した原価 */
  InternalAdjustedAmount: number;
  /** 外注単価 */
  outsourcingUnitPrice: number;
  /** 内製単価 */
  inhouseUnitPrice: number;
  /** 外注金額 */
  outsourcingAmount: number;
  /** 内製金額 */
  inhouseAmount: number;
  /** 内製比率 */
  inhouseRatio: number | string;
  /** 歩留まり */
  yield: number | string;
}

/**
 * 月別データを含むBOMデータ
 */
export interface CBomDataWithMonthly {
  /** 連番 */
  seq: number | string;
  /** アイテムNo */
  ItemNo: string;
  /** アイテム名 */
  ItemName: string;
  /** 使用量（ルートレベル・後方互換性のため保持） */
  Usage?: number;
  /** 単価（ルートレベル・後方互換性のため保持） */
  UnitPrice?: number;
  /** 金額（ルートレベル・後方互換性のため保持） */
  Amount?: number;
  /** 内製利益を考慮した原価（ルートレベル・後方互換性のため保持） */
  InternalAdjustedAmount?: number;
  /** 外注単価（ルートレベル・後方互換性のため保持） */
  outsourcingUnitPrice?: number;
  /** 内製単価（ルートレベル・後方互換性のため保持） */
  inhouseUnitPrice?: number;
  /** 外注金額（ルートレベル・後方互換性のため保持） */
  outsourcingAmount?: number;
  /** 内製金額（ルートレベル・後方互換性のため保持） */
  inhouseAmount?: number;
  /** 内製比率（ルートレベル・後方互換性のため保持） */
  inhouseRatio?: number | string;
  /** 歩留まり（ルートレベル・後方互換性のため保持） */
  yield?: number | string;
  /** 月別データ（キー: "YYYY-MM"形式） */
  monthlyData?: Record<string, MonthlyMaterialData>;
}

/**
 * 仕損費単価計算結果の型
 */
export interface DefectCostResult {
  /** Seq番号 */
  seq: string;
  /** Level */
  level: string;
  /** 累積材料費（累積Amount） */
  cumulativeAmount: Decimal;
  /** 累積C/T（累積CycleTime） */
  cumulativeCycleTime: Decimal;
  /** 累積歩留（累積Yield） */
  cumulativeYield: Decimal;
  /** 仕損費単価 */
  defectCostUnitPrice: Decimal;
}

/**
 * 年月別仕損費単価計算結果
 */
export interface DefectCostResultByMonth {
  /** Seq番号 */
  seq: string;
  /** Level */
  level: string;
  /** 年月別計算結果（キー: "YYYY-MM"形式） */
  monthlyData: Record<string, DefectCostResult>;
}

/**
 * ProcessBOM行データの型（calculation.ts用の簡易型）
 * ※ amount, cycleTime, yieldは年月別データ（Record<string, string>）
 */
export interface ProcessBomRowForCalculation {
  seq?: string;
  level?: string;
  /** 客先指定部品フラグ */
  isCustomerSpecifiedParts?: string | null;
  /** 購入通貨ID */
  purchaseCurrencyId?: string | null;
  /** 年月別金額データ（キー: "YYYY-MM"形式） */
  amountMonthly?: Record<string, string>;
  /** 年月別サイクルタイムデータ（キー: "YYYY-MM"形式） */
  cycleTimeMonthly?: Record<string, string>;
  /** 年月別歩留まりデータ（キー: "YYYY-MM"形式） */
  yieldMonthly?: Record<string, string>;
}

/**
 * 合理化前後サマリーデータ
 */
export interface RationalizationSummaryInputData {
  /** プロセスID（工程ID） */
  processId?: string;
  /** 工程名 */
  itemNo?: string;
  /** 固定資産番号 */
  fixedAssetNumber: string;
  /** 稼働日数 */
  operatingDays: number;
  /** 労働時間 */
  workTime: string;
  /** 勤務体制 */
  shiftSystem: string;
  /** 部品数 */
  partsCount?: number;
  /** 当該工程までの累積原価 (JPY) */
  cumulativeCost?: number;
  /** 選択されたPartNo */
  selectedPartNo?: string;
  /** PartNoごとの生産能力データ */
  productionCapacityByPartNo?: { [partNo: string]: RationalizationProductionCapacityDataByPartNo };
  /** 合理化をする理由 */
  rationalizationReason: string;
  /** 設備あたりの生産性向上対策 */
  productionPerEquipment: string;
  /** 一人あたりの生産性向上対策 */
  productionPerPerson: string;
  /** 非稼働対策 */
  nonOperationMeasures: string;
  /** 非加工対策 */
  nonProcessingMeasures: string;
  /** 現行設備の処置方法 */
  currentEquipmentHandling: string;
  /** 省人化の計画 */
  laborSavingPlan: string;
  /** 合理化による期待効果 */
  expectedEffect: string;
  /** 指標グループ項目 */
  indicatorItems?: RationalizationProfitLossItem[];
  /** 費用グループ項目 */
  costItems?: RationalizationProfitLossItem[];
  /** 費用の通貨ID */
  costCurrencyId?: string;
}

/**
 * 合理化生産能力データ（PartNo別）
 */
export interface RationalizationProductionCapacityDataByPartNo {
  /** 直近3ヶ月平均実績 */
  recentActual: RationalizationProductionCapacityData;
  /** 想定 */
  theoretical: RationalizationProductionCapacityData;
  /** 目論見（合理化後） */
  investmentPlan: RationalizationProductionCapacityData;
}

/**
 * 合理化生産能力データ
 */
export interface RationalizationProductionCapacityData {
  /** 設備台数 */
  equipmentCount?: number;
  /** 歩留 (%) */
  yield?: number;
  /** 非稼働時間 (hr) */
  nonWorkingTime?: number;
  /** サイクルタイム (sec) */
  cycleTime?: number;
  /** 1日あたり必要人員数 (pax) */
  personnelPerDay?: number;
  /** 生産能力 (pcs) ※計算値 */
  productionVolume?: number;
  /** 設備あたり生産性 (pcs) ※計算値 */
  productionPerEquipment?: number;
  /** 1人あたり生産性 (pcs) ※計算値 */
  productionPerPerson?: number;
}

/**
 * 合理化サマリー項目の種類（指標グループ・費用グループ共通）
 */
export type RationalizationProfitLossItemType =
  | "productionQuantity"             // 生産数（指標）
  | "salesQuantity"                  // 売上数量（指標・簡易モード必須）
  | "laborCost"                      // 労務費（費用・簡易モード必須）
  | "repairCost"                     // 修繕費（費用）
  | "consumablesCost"                // 消耗品費（費用）
  | "maintenanceCost"                // 保守・メンテナンス費（費用）
  | "scrapCost"                      // スクラップ費用（費用）
  | "targetEquipmentDepreciation"    // 投資対象設備の償却費（費用・両モード必須）
  | "otherDepreciation"              // その他の償却費（費用・両モード必須）
  | "custom";                        // 自由入力（指標・費用共通）

/**
 * 合理化サマリー項目の単位種別
 */
export type RationalizationProfitLossUnitType = "pcs" | "currency" | "custom";

/**
 * 合理化サマリー項目
 */
export interface RationalizationProfitLossItem {
  /** 項目ID（一意識別子） */
  id: string;
  /** 項目種類 */
  itemType: RationalizationProfitLossItemType;
  /** 項目名（自由入力の場合のみ使用） */
  customItemName?: string;
  /** 単位種別 */
  unitType: RationalizationProfitLossUnitType;
  /** 単位表示名（自由入力の場合のみ使用、"通貨"と入力でローカル通貨表示） */
  customUnit?: string;
  /** 合理化前の月別データ（キー: "YYYY-MM"形式、値: 数値） */
  beforeMonthlyData: Record<string, number>;
  /** 合理化後の月別データ（キー: "YYYY-MM"形式、値: 数値） */
  afterMonthlyData: Record<string, number>;
  /** 合理化前 手動編集済みフラグ（targetEquipmentDepreciation のみ使用。trueの場合は通貨変更時に再計算しない） */
  isManuallyEditedBefore?: boolean;
  /** 合理化後 手動編集済みフラグ（targetEquipmentDepreciation のみ使用。trueの場合は通貨変更時に再計算しない） */
  isManuallyEditedAfter?: boolean;
}

/**
 * 金利マスタ
 */
export interface InterestRateMaster {
  /** 地域コード (PK) */
  areaCode?: string;
  /** 適用年度 (SK) YYYY-MM形式 */
  fiscalPeriod?: string;
  /** 地域名（日本語） */
  areaName?: BilingualName;
  /** 短期金利(%) */
  shortTermRate?: number;
  /** 長期金利(%) */
  longTermRate?: number;
  /** 表示順 */
  order?: number;
  /** 削除フラグ */
  deleted?: boolean;
  /** 作成日時 */
  createDate?: string;
  /** 更新日時 */
  updateDate?: string;
}

// 顧客需要と設備キャパシティの計算詳細
export interface CapacityDetailsData {
  labels: string[];
  salesQuantities: number[];        // 販売個数
  workingDays: number[];             // 稼働日数
  holidays: number[];                // 休日出勤日数
  cycleTimes: number[];              // サイクルタイム(秒)
  productionCapacities: number[];    // 生産可能個数
  availabilityRates: number[];       // 稼働率
  yieldRates: number[];              // 歩留率
  lineCounts: number[];              // ライン数
  capacities: number[];              // キャパ
  surplusCapacities: number[];       // キャパ過剰分
}

// 設備生産数量の計算詳細
export interface ProductionQuantityDetailsData {
  labels: string[];
  estimatedQuantities: number[];         // 出荷必要見込み数
  workingDays: number[];                  // 稼働日数
  holidays: number[];                     // 休日出勤日数
  cycleTimes: number[];                   // サイクルタイム(秒)
  availabilityRates: number[];            // 稼働率
  yieldRates: number[];                   // 歩留率
  lineCounts: number[];                   // ライン数
  workingDayQuantities: number[];         // 稼働日分生産個数
  holidayQuantities: number[];            // 休日分生産個数
  improvedYieldQuantities: number[];      // 歩留改善分(当初/DSは0)
  improvedAvailabilityQuantities: number[]; // 稼働率改善分(当初/DSは0)
  improvedCycleTimeQuantities: number[];  // サイクル短縮分(当初/DSは0)
}

/**
 * システム利用設定データ
 */
export interface SystemAccessUserType {
  /* ユーザID */
  userId: string;
  /* ユーザ名 */
  userName: string;
  /* システム利用可否 */
  isSystemAccessible: boolean;
  /* ユーザロール（権限） */
  userRole?: string | null;
  /* マスタメンテナンス表示有無 */
  isMasterMaintenanceAccessible?: boolean;
  /* 本社経営管理 */
  isHeadquartersManagement?: boolean;
  /* プレ検討会 担当者 */
  isPreReviewAssignee?: boolean;
  /* 本部長設備投資委員会 担当者 */
  isHqReviewAssignee?: boolean;
  /* 会長/副会長/社長 確認 担当者 */
  isPresidentReportAssignee?: boolean;
  /* 登録ユーザ名 */
  createUserName?: string;
  /* 登録日時 */
  createDate?: string;
  /* 更新ユーザ名 */
  updateUserName?: string;
  /* 更新日時 */
  updateDate?: string;
  /* 所属部署一覧（r_user_affiliation 経由、複数所属可） */
  affiliations?: SystemAccessUserAffiliationType[];
}

/**
 * システム利用設定 - 所属部署情報
 */
export interface SystemAccessUserAffiliationType {
  /* 部署ID */
  organizationId: string;
  /* 部署名 */
  organizationName: string;
  /* 削除済みフラグ */
  deleted?: boolean;
}

export interface OrgViewAccessItemType {
  userId: string;
  organizationId: string;
  organizationName: string | null;
  memo: string | null;
  createUserId: string | null;
  createDate: string | null;
}

export interface GeneralPurposeChecklistData {
  /** グループ全体での台数 */
  groupTotalCount: string;
  /** 拠点での台数 */
  siteCount: string;
  /** 保存部門担当の承認を得ているか ("はい" | "いいえ" | "") */
  storageDepartmentApproval: string;
}

/**
 * 月別為替レート（r_currency から取得）
 */
export interface MonthlyRate {
  /** 通貨ID（"currency002" 等） */
  currencyId: string;
  /** 通貨名（"USD" 等） */
  currencyName: string;
  /** 為替レート（1通貨 = X円） */
  exchangeRate: number;
  /** 適用月（"YYYY-MM-DD" 形式、日は常に01） */
  rateMonth: string;
}

/**
 * PJ検索結果の1件（No141 過去ベンチマーク検索）
 */
export interface PastProjectSearchItem {
  projectId: string;
  reviewDate?: string;
  projectName: string;
  partNo: string;
  approveDay: string;
  approvalStatus: string;
  /** パーツNoごとの詳細（partNo・displayPartNoの対応、No.435対応） */
  partNoDetails?: PartNoDetail[];
}

/**
 * ベンチマーク値（No141 過去ベンチマーク参照）
 */
export interface BenchmarkValues {
  yield?: number;
  operatingRate?: number;
  cycleTime?: number;
  personnelPerDay?: number;
  productionVolume?: number;
  productionPerEquipment?: number;
  productionPerPerson?: number;
}

/**
 * ベンチマーク1件のデータ（設備・ライン共通）
 */
export interface BenchmarkItem {
  inputType: "equipment" | "line";
  equipmentId?: string;
  equipmentName?: string;
  processCode?: string;
  productionLineId?: string;
  productionLineName?: string;
  benchmarkMonth: string;
  actualBest: BenchmarkValues;
  theoretical: BenchmarkValues;
  investmentPlan: BenchmarkValues;
  benchmarkByPartNo?: { [partNo: string]: BenchmarkDataByPartNo };
  selectedPartNo?: string;
  /** ギャップ分析（テキスト） */
  gapAnalysis?: string;
  /** 設備あたり生産性向上対策（テキスト） */
  productionPerEquipment?: string;
  /** 1人あたり生産性向上対策（テキスト） */
  productionPerPerson?: string;
  /** 非稼働対策（テキスト） */
  nonOperationMeasures?: string;
  /** 歩留向上対策（テキスト） */
  yieldMeasures?: string;
}

/**
 * getProjectAdditionalInfoById のレスポンス全体
 */
export interface ProjectBenchmarkResponse {
  projectId: string;
  benchmarks: BenchmarkItem[];
}

/**
 * Popover下部に表示する選択済み過去ベンチマーク
 */
export interface SelectedPastBenchmark {
  projectId: string;
  projectName: string;
  inputType: "equipment" | "line";
  equipmentId?: string;
  equipmentName?: string;
  processCode?: string;
  productionLineId?: string;
  productionLineName?: string;
  benchmarkMonth: string;
  actualBest: BenchmarkValues;
  theoretical: BenchmarkValues;
  investmentPlan: BenchmarkValues;
  benchmarkByPartNo?: { [partNo: string]: BenchmarkDataByPartNo };
  /** ギャップ分析（テキスト） */
  gapAnalysis?: string;
  /** 設備あたり生産性向上対策（テキスト） */
  productionPerEquipment?: string;
  /** 1人あたり生産性向上対策（テキスト） */
  productionPerPerson?: string;
  /** 非稼働対策（テキスト） */
  nonOperationMeasures?: string;
  /** 歩留向上対策（テキスト） */
  yieldMeasures?: string;
}

/**
 * CEAS 状況報告（検収管理）画面 — データモデル
 * =================================================
 * UIモック「設備投資申請 状況報告 v2（改善メモ版）」のデータ契約。
 * 列は3区分に分かれる（詳細は CEAS状況報告_データ取得調査依頼.md を参照）:
 *
 *   A. 投資申請由来（参照・編集不可） … 承認完遂後の申請データから取得
 *   B. 状況報告 入力（本画面で手入力・保存対象）
 *   C. 自動計算（保存不要・表示のみ）
 */

/** 判定区分（C: 自動計算） */
export type Judge = 'ok' | 'over' | 'none';

/** 通貨コード（メーカー内訳・発注・検収で使用） */
export type CurrencyCode = 'YN' | 'JPY' | '円' | 'USD' | 'CNY' | 'KRW' | 'RMB' | 'PHP';

/**
 * 設備メーカー内訳の1行（B: 入力）。
 * 1案件に対し複数行 = 別テーブル（1対多）想定。
 */
export interface Maker {
  /** メーカー名 */
  name: string;
  /** 通貨 */
  currency: CurrencyCode;
  /** 金額（千・桁区切り文字列） */
  amount: string;
  /** 会社コード */
  code: string;
  /** 会社名 */
  company: string;
}

/** 状況報告テーブルの1行（1案件） */
export interface StatusRow {
  /** 行の内部ID（データ設計外・UI用） */
  id: string;

  // ─────────────── A. 投資申請由来（参照・編集不可） ───────────────
  /** 設備計画No.（案件の主キー / 結合キー候補） */
  planNo: string;
  /** 設備件名 */
  name: string;
  /** 製造本部名 */
  hq: string;
  /** 部門名等 */
  dept: string;
  /** 事業部名等 */
  unit: string;
  /** 設備申請時期 (YYYY/M) */
  applyPeriod: string;
  /** 申請金額（百万円）※単位注意: C列の円換算は千円 */
  applyAmountM: number | null;
  /** 会社コード（複数可, 例 "7U,7L"） */
  coCode: string;
  /** 会社名 */
  companies: string[];

  // ─────────────── B. 状況報告 入力（手入力・保存対象） ───────────────
  /** 設備申請NO. ★申請由来の可能性あり＝A列かも（要確認） */
  applyNo: string;
  /** 発注時期（実施）YYYY-MM-DD */
  orderDate: string;
  /** 発注先会社名（複数可） */
  orderCompanies: string[];
  /** 設備メーカー内訳 */
  makers: Maker[];
  /** 発注通貨 */
  orderCurrency: CurrencyCode;
  /** 発注金額（千） */
  orderAmount: string;
  /** 発注元会社コード */
  orderSrcCode: string;
  /** 発注元会社名 */
  orderSrcCompany: string;
  /** 検収時期（実施）YYYY-MM-DD */
  inspectDate: string;
  /** 検収通貨（複数可） */
  inspectCurrencies: CurrencyCode[];
  /** 検収金額（原貨・千） */
  inspectAmountOrig: string;
  /** 計上/支払予定時期 YYYY-MM */
  payPeriod: string;
  /** 計上元会社コード */
  paySrcCode: string;
  /** 計上元会社名 */
  paySrcCompany: string;
  /** 備考（上限1,000文字） */
  remarks: string;
  /** 検収完了フラグ */
  done: boolean;

  // ─────────────── C. 自動計算（表示のみ） ───────────────
  /**
   * 検収額（円換算・千）。
   * TODO: 現状は保持値。実装では 検収通貨×金額 を為替レートで円換算する。
   *       レート取得元・基準日（発注時/検収時/月次固定）が論点。
   */
  inspectAmountJpy: string;
  /**
   * 申請額判定。
   * TODO: 実装では inspectAmountJpy(千円) と applyAmountM(百万円→千円換算) を比較。
   *       単位換算に注意（applyAmountM × 1000 = 千円）。
   */
  judge: Judge;

  // ─────────────── 暫定項目（バックエンド未確定・フロント先行実装） ───────────────
  // 超過時の「追加申請済み確認」チェック・「超過理由」テキスト。
  // TODO: 保存先・永続化方式は要データ設計（バックエンド確定後にDTO変換層で対応）。
  //       それまではフロントの状態としてのみ保持する。
  /** 超過時の追加申請済み確認チェック */
  additionalApplied?: boolean;
  /** 超過理由（超過時は入力必須） */
  overReason?: string;

  /** 未保存フラグ（UI状態のみ・永続化しない） */
  dirty: boolean;
}

/** 一覧のプリセットフィルタ */
export type StatusFilter = 'all' | 'uninspected' | 'over';

/**
 *  材料データ（プロセスに設定する情報）
 *  Process Codeを持たないため、ReactFlowのnodes/edgesには乗らず、
 *  紐づくBomFlowNodeData.materialsに内包される付属データ
 */
export interface BomMaterial {
  /** React key用のローカルID（crypto.randomUUID()等で生成） */
  id: string;
  /** 部品番号（Item No.） */
  itemNo: string;
  /** 品名（Item Name） */
  itemName: string;
}
/**
 * BOM作成プロセスノードデータ
 */
export interface BomFlowNodeData {
  /** モデルNo（Level1ノードのみ入力する運用。Level2以降は空欄） */
  modelNo: string;
  /** 工程コード（Process Code）。同一モーダル内での重複チェック対象 */
  processCode: string;
  /** 工程の部品番号（Item No.） */
  itemNo: string;
  /** 工程名（Item Name、画面表示用） */
  itemName: string;
  /** この工程に紐づく材料一覧。キャンバス上には描画しない */
  materials: BomMaterial[];
}

/** BOM作成モーダルのReactFlowノード（reactflow標準Node<T>にBomFlowNodeDataを適用） */
export type BomFlowNode = Node<BomFlowNodeData>;
/** BOM作成モーダルのReactFlowエッジ（source/target/idのみで足りるため標準Edge型のまま） */
export type BomFlowEdge = Edge;

/**
 * FAQデータの型定義
 * jsonファイルから取得したデータをキャストする想定
 * 基本空入力はNG（サブカテゴリだけ可）
 */
export interface FAQItem {
  /** キー */
  id: number;
  /** 質問文 */
  question: { ja: string; en: string };
  /** 回答文 */
  answer: { ja: string; en: string };
  /** カテゴリ（対象画面名などを想定） */
  category: {
    /** カテゴリ名 */
    categoryName: { ja: string; en: string };
    /** サブカテゴリ名 空でも可*/
    subCategory: { ja: string; en: string };
  };
  /** 掲載日 */
  publishDate: string;
  /** 更新日(更新してない場合は掲載日と同じ日付を設定してほしい) */
  updateDate: string;
}